import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FieldLabel } from '../components/FieldLabel';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { sanitizeString, decodeHtml } from '../utils/security';
import { formatTechServiceLocation } from '../utils/techServiceHelpers';
import { createUser } from '../services/usersService';
import {
  ORDER_CREATION_PERMISSION_OPTIONS,
  ORDER_CREATION_PERMISSION_DEFAULT,
} from '../constants/orderCreationPermission';
import OrderCreationPermissionBadge from '../components/OrderCreationPermissionBadge';
import CariHareketlerPanel from '../components/CariHareketlerPanel';
import TeknikServisStokDetayPanel from '../components/TeknikServisStokDetayPanel';
import KonsinyeCariLimitCard from '../components/KonsinyeCariLimitCard';
import NotificationStack from '../components/NotificationStack';
import {
  fetchTechServiceDetail,
  createTechService,
  updateTechService,
  fetchTechServicesSelect,
  invalidateTechServicesSelectCache,
} from '../services/techServicesService';
import {
  fetchCities,
  fetchDistrictsByCity,
} from '../services/locationsService';
import { fetchBanks } from '../services/banksService';
import {
  fetchServiceAreasByTechService,
  checkDistrictsBatch,
  createMultipleServiceAreas,
  deleteServiceArea,
  reassignServiceArea,
} from '../services/serviceAreasService';
import { fetchCategories } from '../services/categoriesService';
import { fetchTechniciansByTechService } from '../services/techniciansService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import { ServiceAreaCategoryDetail } from '../components/ServiceAreaCategoryDetail';
import { AuthorizedAreaProductTree } from '../components/AuthorizedAreaProductTree';
import DistrictAuthorizationInfoModal from '../components/DistrictAuthorizationInfoModal';
import {
  emptySelection,
  toApiPayload,
  batchCheckOptions,
  buildProductsByLeaf,
} from '../utils/serviceAreaSelection';
import { fetchAllContentsByPaginating } from '../services/productsService';

const initialFormState = {
  id: '',
  ad: '',
  kod: '',
  // İletişim bilgileri
  mail: '',
  yetkiliKisi: '',
  yetkiliTelefon: '',
  // Lokasyon bilgileri
  adres: '',
  cityId: '',
  districtId: '',
  enlem: '',
  boylam: '',
  zoom: 15,
  // Şirket bilgileri (array)
  sirketBilgileri: [],
  // Banka bilgileri (array)
  bankaBilgileri: [],
  // Diğer
  telefon: '',
  faks: '',
  katilmaTarihi: '',
  aciklama: '',
  aktif: true,
  isTest: false,
};

const TECH_SERVICE_TABS = [
  { key: 'service', label: 'Servis Bilgileri' },
  { key: 'cariHareketler', label: 'Cari Hareketler' },
  { key: 'stok', label: 'Stok' },
  { key: 'location', label: 'Lokasyon Bilgileri' },
  { key: 'company', label: 'Şirket Bilgileri' },
  { key: 'bank', label: 'Banka Bilgileri' },
  { key: 'authorized', label: 'Yetkili Alanlar' },
  { key: 'technicians', label: 'Teknisyenler' },
  { key: 'other', label: 'Diğer' },
];

const formatOlusturmaTarihiTr = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
};

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) {
    return fallback;
  }

  if (error?.status === 404) {
    return 'API endpoint bulunamadı.';
  }

  if (error?.status === 401) {
    return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  }
  
  if (error?.status === 403) {
    return 'Bu işlem için yetkiniz yok.';
  }

  const hatalar = error?.data?.hatalar;
  if (Array.isArray(hatalar) && hatalar.length > 0) {
    const hataMesajlari = hatalar
      .map(h => h.mesaj || h.message)
      .filter(Boolean);
    
    if (hataMesajlari.length > 0) {
      return hataMesajlari.join('. ');
    }
  }

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (error?.message === 'Failed to fetch' || message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const normalizeAuthorizedCategoryList = (response) => {
  const list = response?.veri?.kategoriler || response?.veri || [];
  return Array.isArray(list) ? list : [];
};

const TechServiceFormPage = ({ mode }) => {
  const { confirm } = useConfirmDialog();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedMode = mode || (id ? 'edit' : 'create');
  const [currentMode, setCurrentMode] = useState(resolvedMode);
  const [formState, setFormState] = useState(initialFormState);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(resolvedMode === 'edit');
  const [draftApplied, setDraftApplied] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState({ lat: null, lng: null });
  const [activeSectionTab, setActiveSectionTab] = useState(TECH_SERVICE_TABS[0].key);
  const servicesRef = useRef([]);
  const initialFormStateRef = useRef(null);

  const reloadServices = useCallback(async () => {
    try {
      const response = await fetchTechServicesSelect();
      const list = response?.veri?.teknikServisler || [];
      servicesRef.current = list;
    } catch (error) {
      console.error('Teknik servis listesi yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    reloadServices();
  }, [reloadServices]);

  const checkDuplicateName = useCallback(
    (name, excludeId) => {
      if (!name) return null;
      const normalized = name.trim().toLowerCase();
      return servicesRef.current.find((service) => {
        const serviceName = (service.ad || '').trim().toLowerCase();
        const serviceId = service.id || service._id;
        if (!serviceName || serviceName !== normalized) return false;
        if (excludeId && String(serviceId) === String(excludeId)) return false;
        return true;
      });
    },
    []
  );
  
  // İl/İlçe için state'ler
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [illerLoading, setIllerLoading] = useState(false);
  const [ilcelerLoading, setIlcelerLoading] = useState(false);
  
  // Banka için state'ler
  const [bankalar, setBankalar] = useState([]);
  const [bankalarLoading, setBankalarLoading] = useState(false);
  
  // Şirket bilgileri modal state'leri
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companyModalMode, setCompanyModalMode] = useState('create');
  const [editingCompanyIndex, setEditingCompanyIndex] = useState(null);
  const [companyFormState, setCompanyFormState] = useState({
    sirketTipi: '',
    sirketAdi: '',
    vergiDairesi: '',
    vergiNumarasi: '',
    sirketAdresi: '',
  });
  
  // Banka bilgileri modal state'leri
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [bankModalMode, setBankModalMode] = useState('create');
  const [editingBankIndex, setEditingBankIndex] = useState(null);
  const [bankFormState, setBankFormState] = useState({
    bankaId: '',
    hesapAdi: '',
    iban: '',
  });
  
  // Google Maps konum seçici için state'ler
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 39.9334, lng: 32.8597 });
  const [mapZoom, setMapZoom] = useState(6);
  const [pinVisible, setPinVisible] = useState(false);
  const [pinPosition, setPinPosition] = useState({ lat: null, lng: null });
  const [originalLocation, setOriginalLocation] = useState({ lat: null, lng: null, zoom: null });
  
  // Map instance referansı
  const mapInstanceRef = React.useRef(null);
  const markerInstanceRef = React.useRef(null);
  
  // Yetkili alanlar state'leri
  const [serviceAreas, setServiceAreas] = useState([]);
  const [serviceAreasLoading, setServiceAreasLoading] = useState(false);
  const [isAuthorizedAreaModalOpen, setIsAuthorizedAreaModalOpen] = useState(false);
  const [authorizedAreaFormState, setAuthorizedAreaFormState] = useState({
    cityId: '',
    selectedDistricts: [],
    areaSelection: emptySelection(),
  });
  const [authorizedAreaUrunContents, setAuthorizedAreaUrunContents] = useState([]);
  const [authorizedAreaDistricts, setAuthorizedAreaDistricts] = useState([]);
  const [authorizedAreaDistrictsLoading, setAuthorizedAreaDistrictsLoading] = useState(false);
  const [districtServiceAreaChecks, setDistrictServiceAreaChecks] = useState({});
  const [reassignLoadingDistrictId, setReassignLoadingDistrictId] = useState(null);
  const [districtChecksRefreshKey, setDistrictChecksRefreshKey] = useState(0);
  const [districtInfoModal, setDistrictInfoModal] = useState(null);
  const [authorizedAreaProductCategories, setAuthorizedAreaProductCategories] = useState([]);

  /** GET /api/tech-services/:id → teknikServis.teknikServisKullanicilari (rol: tech-service) */
  const [teknikServisKullanicilari, setTeknikServisKullanicilari] = useState([]);
  const [isPanelUserModalOpen, setIsPanelUserModalOpen] = useState(false);
  const [panelUserModalState, setPanelUserModalState] = useState({
    kullaniciAdi: '',
    sifre: '',
  });
  const [panelUserModalError, setPanelUserModalError] = useState('');
  const [panelUserModalSubmitting, setPanelUserModalSubmitting] = useState(false);
  const [serviceTabTechnicians, setServiceTabTechnicians] = useState([]);
  const [serviceTabTechniciansLoading, setServiceTabTechniciansLoading] = useState(false);
  const [serviceTabTechniciansError, setServiceTabTechniciansError] = useState('');
  const [isTechnicianModalOpen, setIsTechnicianModalOpen] = useState(false);
  const [technicianModalState, setTechnicianModalState] = useState({
    kullaniciAdi: '',
    sifre: '',
    orderCreationPermission: ORDER_CREATION_PERMISSION_DEFAULT,
  });
  const [technicianModalError, setTechnicianModalError] = useState('');
  const [technicianModalSubmitting, setTechnicianModalSubmitting] = useState(false);

  // İlleri yükle
  useEffect(() => {
    const loadIller = async () => {
      setIllerLoading(true);
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        setIller([]);
        if (window.showNotification) {
          window.showNotification('error', 'İller yüklenemedi.');
        }
      } finally {
        setIllerLoading(false);
      }
    };
    loadIller();
  }, []);

  // İlçeleri yükle (cityId değiştiğinde) - CustomerManagementPage ile TAM AYNI
  useEffect(() => {
    const loadDistricts = async () => {
      if (!formState.cityId || formState.cityId.trim() === '') {
        console.log('[TechServiceFormPage] No cityId, clearing districts');
        setIlceler([]);
        return;
      }
      
      const cityIdNum = parseInt(formState.cityId, 10);
      if (isNaN(cityIdNum)) {
        console.warn('[TechServiceFormPage] Invalid cityId:', formState.cityId);
        setIlceler([]);
        return;
      }
      
      console.log('[TechServiceFormPage] Loading districts for cityId:', cityIdNum);
      setIlcelerLoading(true);
      try {
        const response = await fetchDistrictsByCity(cityIdNum);
        console.log('[TechServiceFormPage] fetchDistrictsByCity response:', response);
        console.log('[TechServiceFormPage] response.veri:', response?.veri);
        console.log('[TechServiceFormPage] response.veri?.ilceler:', response?.veri?.ilceler);
        console.log('[TechServiceFormPage] response.veri?.districts:', response?.veri?.districts);
        
        // Farklı response formatlarını kontrol et
        let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri?.ilceList || response?.veri || [];
        
        // Eğer list bir array değilse, kontrol et
        if (!Array.isArray(list)) {
          console.warn('[TechServiceFormPage] Districts list is not an array:', list);
          list = [];
        }
        
        // API formatı: { id: number, adi: string, cityId: number }
        const serializedList = list.map((ilce) => {
          return {
            id: ilce.id || ilce._id,
            adi: ilce.adi || ilce.ad || ilce.name,
            ad: ilce.adi || ilce.ad || ilce.name, // Geriye dönük uyumluluk için
            name: ilce.name || ilce.adi || ilce.ad, // Geriye dönük uyumluluk için
            cityId: ilce.cityId
          };
        });
        
        console.log('[TechServiceFormPage] Districts list (final, serialized):', serializedList);
        console.log('[TechServiceFormPage] Districts list length:', serializedList.length);
        
        setIlceler(serializedList);
      } catch (err) {
        console.error('[TechServiceFormPage] İlçeler yüklenemedi:', err);
        setIlceler([]);
      } finally {
        setIlcelerLoading(false);
      }
    };
    
    loadDistricts();
  }, [formState.cityId]);

  // Bankaları yükle
  useEffect(() => {
    const loadBankalar = async () => {
      setBankalarLoading(true);
      try {
        const response = await fetchBanks();
        if (response?.basarili && response?.veri?.bankalar && Array.isArray(response.veri.bankalar)) {
          setBankalar(response.veri.bankalar);
        } else if (response?.basarili && response?.veri && Array.isArray(response.veri)) {
          setBankalar(response.veri);
        }
      } catch (err) {
        console.error('Bankalar yüklenemedi:', err);
      } finally {
        setBankalarLoading(false);
      }
    };
    loadBankalar();
  }, []);

  // Quick modal draft (ad / city / district)
  useEffect(() => {
    const draft = location.state?.draft;
    if (draft && !draftApplied) {
      setFormState((prev) => ({
        ...prev,
        ad: draft.ad || prev.ad,
        cityId: draft.cityId || prev.cityId,
        districtId: draft.districtId || prev.districtId,
      }));
      setDraftApplied(true);
    }
  }, [location.state, draftApplied]);

  useEffect(() => {
    if (formState.enlem && formState.boylam) {
      const lat = parseFloat(formState.enlem);
      const lng = parseFloat(formState.boylam);
      setSelectedLocation({
        lat: Number.isNaN(lat) ? null : lat,
        lng: Number.isNaN(lng) ? null : lng,
      });
    } else {
      setSelectedLocation({ lat: null, lng: null });
    }
  }, [formState.enlem, formState.boylam]);

  // Breadcrumb için teknik servis adını güncelle (formState.ad değiştiğinde)
  useEffect(() => {
    if (currentMode === 'edit' && formState.ad) {
      window.currentTechServiceName = formState.ad;
    }
  }, [formState.ad, currentMode]);

  const panelUserModalTechLocation = useMemo(() => {
    const il = iller.find((x) => String(x.id || x._id) === String(formState.cityId));
    const cityName = il?.name || il?.ad || '';
    const ilce = ilceler.find((x) => String(x.id || x._id) === String(formState.districtId));
    const districtName = ilce?.adi || ilce?.ad || ilce?.name || '';
    return formatTechServiceLocation({ cityName, districtName });
  }, [iller, ilceler, formState.cityId, formState.districtId]);

  const isPanelUserModalValid = useMemo(() => {
    const username = panelUserModalState.kullaniciAdi?.trim() || '';
    if (username.length < 3 || username.length > 50) return false;
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return false;
    const password = panelUserModalState.sifre || '';
    if (password.length < 6 || password.length > 100) return false;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    return hasLowercase && hasUppercase && hasNumber;
  }, [panelUserModalState]);

  const isTechnicianModalValid = useMemo(() => {
    const username = technicianModalState.kullaniciAdi?.trim() || '';
    if (username.length < 3 || username.length > 50) return false;
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return false;
    const password = technicianModalState.sifre || '';
    if (password.length < 6 || password.length > 100) return false;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    return hasLowercase && hasUppercase && hasNumber;
  }, [technicianModalState]);

  const refreshTeknikServisKullanicilari = useCallback(async () => {
    if (!id || currentMode !== 'edit') return;
    try {
      const response = await fetchTechServiceDetail(id);
      if (response?.basarili && response?.veri?.teknikServis) {
        const ts = response.veri.teknikServis;
        setTeknikServisKullanicilari(
          Array.isArray(ts.teknikServisKullanicilari) ? ts.teknikServisKullanicilari : []
        );
      }
    } catch (err) {
      console.error('[TechServiceFormPage] Panel kullanıcıları yenilenemedi:', err);
    }
  }, [id, currentMode]);

  const closePanelUserModal = useCallback(() => {
    setIsPanelUserModalOpen(false);
    setPanelUserModalState({ kullaniciAdi: '', sifre: '' });
    setPanelUserModalError('');
  }, []);

  const handlePanelUserModalSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!id || currentMode !== 'edit') return;

      setPanelUserModalSubmitting(true);
      setPanelUserModalError('');

      try {
        const username = panelUserModalState.kullaniciAdi?.trim();
        if (!username || username.length < 3) {
          setPanelUserModalError('Kullanıcı adı en az 3 karakter olmalıdır');
          return;
        }
        if (username.length > 50) {
          setPanelUserModalError('Kullanıcı adı en fazla 50 karakter olabilir');
          return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
          setPanelUserModalError('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir');
          return;
        }

        const password = panelUserModalState.sifre || '';
        if (password.length < 6) {
          setPanelUserModalError('Şifre en az 6 karakter olmalıdır');
          return;
        }
        if (password.length > 100) {
          setPanelUserModalError('Şifre en fazla 100 karakter olabilir');
          return;
        }
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        if (!hasLowercase || !hasUppercase || !hasNumber) {
          setPanelUserModalError(
            'Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermelidir'
          );
          return;
        }

        const payload = {
          kullaniciAdi: username,
          sifre: password,
          rol: 'tech-service',
          techService: id,
        };

        const response = await createUser(payload);

        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Kullanıcı oluşturulamadı');
        }

        if (window.showNotification) {
          window.showNotification('success', 'Panel kullanıcısı oluşturuldu');
        }

        closePanelUserModal();
        await refreshTeknikServisKullanicilari();
      } catch (submitError) {
        console.error('[TechServiceFormPage] Panel kullanıcı oluşturma:', submitError);
        setPanelUserModalError(getErrorMessage(submitError, 'İşlem gerçekleştirilemedi'));
      } finally {
        setPanelUserModalSubmitting(false);
      }
    },
    [id, currentMode, panelUserModalState, closePanelUserModal, refreshTeknikServisKullanicilari]
  );

  const reloadTechniciansSilently = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetchTechniciansByTechService(id, { aktif: 'all' });
      const list = res?.veri?.teknisyenler || [];
      setServiceTabTechnicians(Array.isArray(list) ? list : []);
      setServiceTabTechniciansError('');
    } catch (err) {
      const msg = getErrorMessage(err, 'Teknisyen listesi yenilenemedi');
      setServiceTabTechniciansError(msg);
      if (window.showNotification) {
        window.showNotification('error', msg);
      }
    }
  }, [id]);

  const closeTechnicianModal = useCallback(() => {
    setIsTechnicianModalOpen(false);
    setTechnicianModalState({
      kullaniciAdi: '',
      sifre: '',
      orderCreationPermission: ORDER_CREATION_PERMISSION_DEFAULT,
    });
    setTechnicianModalError('');
  }, []);

  const handleTechnicianModalSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!id || currentMode !== 'edit') return;

      setTechnicianModalSubmitting(true);
      setTechnicianModalError('');

      try {
        const username = technicianModalState.kullaniciAdi?.trim();
        if (!username || username.length < 3) {
          setTechnicianModalError('Kullanıcı adı en az 3 karakter olmalıdır');
          return;
        }
        if (username.length > 50) {
          setTechnicianModalError('Kullanıcı adı en fazla 50 karakter olabilir');
          return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
          setTechnicianModalError('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir');
          return;
        }

        const password = technicianModalState.sifre || '';
        if (password.length < 6) {
          setTechnicianModalError('Şifre en az 6 karakter olmalıdır');
          return;
        }
        if (password.length > 100) {
          setTechnicianModalError('Şifre en fazla 100 karakter olabilir');
          return;
        }
        const hasLowercase = /[a-z]/.test(password);
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        if (!hasLowercase || !hasUppercase || !hasNumber) {
          setTechnicianModalError(
            'Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermelidir'
          );
          return;
        }

        const payload = {
          kullaniciAdi: username,
          sifre: password,
          rol: 'tech-person',
          techService: id,
          orderCreationPermission:
            technicianModalState.orderCreationPermission || ORDER_CREATION_PERMISSION_DEFAULT,
        };

        const response = await createUser(payload);

        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Kullanıcı oluşturulamadı');
        }

        if (window.showNotification) {
          window.showNotification('success', 'Teknisyen hesabı oluşturuldu');
        }

        closeTechnicianModal();
        await reloadTechniciansSilently();
      } catch (submitError) {
        console.error('[TechServiceFormPage] Teknisyen hesabı oluşturma:', submitError);
        setTechnicianModalError(getErrorMessage(submitError, 'İşlem gerçekleştirilemedi'));
      } finally {
        setTechnicianModalSubmitting(false);
      }
    },
    [
      id,
      currentMode,
      technicianModalState,
      closeTechnicianModal,
      reloadTechniciansSilently,
    ]
  );

  // Yetkili alanları yükle
  const loadServiceAreas = useCallback(async (techServiceId) => {
    // fetchDistrictsByCity import edilmeli
    if (!techServiceId) {
      console.log('[loadServiceAreas] TechServiceId yok:', techServiceId);
      return;
    }
    
    console.log('[loadServiceAreas] Başlatılıyor, techServiceId:', techServiceId);
    setServiceAreasLoading(true);
    try {
      const response = await fetchServiceAreasByTechService(techServiceId);
      console.log('[loadServiceAreas] API Response:', response);
      console.log('[loadServiceAreas] Response basarili:', response?.basarili);
      console.log('[loadServiceAreas] Response veri:', response?.veri);
      console.log('[loadServiceAreas] Yetki alanları:', response?.veri?.yetkiAlanlari);
      
      // Response formatını kontrol et - farklı formatlar olabilir
      let areas = [];
      if (response?.basarili && response?.veri?.yetkiAlanlari) {
        areas = response.veri.yetkiAlanlari;
      } else if (response?.veri?.yetkiAlanlari) {
        areas = response.veri.yetkiAlanlari;
      } else if (Array.isArray(response?.veri)) {
        areas = response.veri;
      } else if (Array.isArray(response?.yetkiAlanlari)) {
        areas = response.yetkiAlanlari;
      }
      
      if (!Array.isArray(areas)) {
        console.warn('[loadServiceAreas] Areas is not an array:', areas);
        areas = [];
      }
      
      console.log('[loadServiceAreas] Yetki alanları bulundu, sayı:', areas.length);
      console.log('[loadServiceAreas] İlk yetki alanı örneği:', areas[0]);
      
      if (areas.length > 0) {
        // İl ve ilçe bilgilerini yükle (eğer populate edilmemişse)
        const areasWithDetails = await Promise.all(areas.map(async (area) => {
          const areaCopy = { ...area };
          
          // Eğer il bilgisi populate edilmemişse, cityId'den yükle
          if (!areaCopy.il && areaCopy.cityId) {
            const cityInfo = iller.find(il => String(il.id || il._id) === String(areaCopy.cityId));
            if (cityInfo) {
              areaCopy.il = {
                id: cityInfo.id || cityInfo._id,
                name: cityInfo.name || cityInfo.ad,
                ad: cityInfo.name || cityInfo.ad,
              };
              console.log('[loadServiceAreas] İl bilgisi eklendi:', areaCopy.il);
            } else {
              console.log('[loadServiceAreas] İl bulunamadı, cityId:', areaCopy.cityId, 'iller:', iller);
            }
          }
          
          // Eğer ilçe bilgisi populate edilmemişse, districtId'den yükle
          if (!areaCopy.ilce && areaCopy.districtId && areaCopy.cityId) {
            try {
              const districtsResponse = await fetchDistrictsByCity(areaCopy.cityId);
              let list = districtsResponse?.veri?.ilceler || districtsResponse?.veri?.districts || districtsResponse?.veri || [];
              if (Array.isArray(list)) {
                const districtInfo = list.find(ilce => String(ilce.id || ilce._id) === String(areaCopy.districtId));
                if (districtInfo) {
                  areaCopy.ilce = {
                    id: districtInfo.id || districtInfo._id,
                    adi: districtInfo.adi || districtInfo.ad || districtInfo.name,
                    ad: districtInfo.adi || districtInfo.ad || districtInfo.name,
                    name: districtInfo.name || districtInfo.adi || districtInfo.ad,
                  };
                  console.log('[loadServiceAreas] İlçe bilgisi eklendi:', areaCopy.ilce);
                } else {
                  console.log('[loadServiceAreas] İlçe bulunamadı, districtId:', areaCopy.districtId);
                }
              }
            } catch (err) {
              console.error('[loadServiceAreas] İlçe yüklenemedi:', err);
            }
          }
          
          return areaCopy;
        }));
        
        console.log('[loadServiceAreas] Yetki alanları detaylarla:', areasWithDetails);
        console.log('[loadServiceAreas] Setting serviceAreas with', areasWithDetails.length, 'items');
        setServiceAreas(areasWithDetails);
      } else {
        console.log('[loadServiceAreas] Yetki alanları bulunamadı veya boş');
        setServiceAreas([]);
      }
    } catch (error) {
      console.error('[loadServiceAreas] Hata:', error);
      console.error('[loadServiceAreas] Error details:', {
        message: error?.message,
        data: error?.data,
        response: error?.response,
        stack: error?.stack
      });
      
      // Backend'den "Maximum call stack size exceeded" hatası geliyorsa
      // Bu backend'de bir sorun olduğunu gösterir (muhtemelen recursive populate veya circular reference)
      if (error?.message?.includes('Maximum call stack size exceeded') || 
          error?.data?.mesaj?.includes('Maximum call stack size exceeded')) {
        console.error('[loadServiceAreas] Backend hatası: Maximum call stack size exceeded - Bu backend\'de bir sorun olduğunu gösterir. Backend ekibine bildirin.');
        // Kullanıcıya sessizce hata göster, sayfa çalışmaya devam etsin
        if (window.showNotification) {
          window.showNotification('warning', 'Yetkili alanlar yüklenirken bir hata oluştu. Sayfa çalışmaya devam ediyor.');
        }
      }
      
      setServiceAreas([]);
    } finally {
      setServiceAreasLoading(false);
    }
  }, [iller]);

  // Edit modunda teknik servis detayını yükle
  useEffect(() => {
    const loadTechService = async () => {
      if (currentMode !== 'edit' || !id) {
        setLoading(false);
        setTeknikServisKullanicilari([]);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetchTechServiceDetail(id);
        if (response?.basarili && response?.veri?.teknikServis) {
          const techService = response.veri.teknikServis;

          setTeknikServisKullanicilari(
            Array.isArray(techService.teknikServisKullanicilari)
              ? techService.teknikServisKullanicilari
              : []
          );
          
          // Konum bilgisini oku
          const konumBilgisi = techService.konum || {};
          const koordinat = konumBilgisi.koordinat || {};
          const enlem = koordinat.enlem !== undefined ? koordinat.enlem : (techService.enlem !== undefined ? techService.enlem : '');
          const boylam = koordinat.boylam !== undefined ? koordinat.boylam : (techService.boylam !== undefined ? techService.boylam : '');
          const zoom = konumBilgisi.zoom !== undefined ? konumBilgisi.zoom : (enlem && boylam ? 15 : 6);
          
          // Şirket bilgilerini formatla
          // API'den gelen veriyi decode et (HTML entity'leri düzelt)
          const sirketBilgileri = (techService.sirketBilgileri || []).map(sirket => ({
            sirketTipi: decodeHtml(sirket.sirketTipi || ''),
            sirketAdi: decodeHtml(sirket.sirketAdi || ''),
            vergiDairesi: decodeHtml(sirket.vergiDairesi || ''),
            vergiNumarasi: decodeHtml(sirket.vergiNumarasi || ''),
            sirketAdresi: decodeHtml(sirket.sirketAdresi || ''),
          }));
          
          // Banka bilgilerini formatla
          const bankaBilgileri = (techService.bankaBilgileri || []).map(banka => {
            // bankaId obje olabilir (_id, id, name içerebilir) veya direkt string ID olabilir
            let bankaIdValue = '';
            if (banka.bankaId) {
              if (typeof banka.bankaId === 'object') {
                bankaIdValue = banka.bankaId._id || banka.bankaId.id || '';
              } else {
                bankaIdValue = String(banka.bankaId);
              }
            }
            return {
              bankaId: bankaIdValue,
            hesapAdi: banka.hesapAdi || '',
            iban: banka.iban || '',
            };
          });
          
          const loadedState = {
            id: techService.id || techService._id,
            ad: techService.ad || '',
            kod: techService.kod != null && techService.kod !== '' ? String(techService.kod) : '',
            mail: techService.mail || '',
            yetkiliKisi: techService.yetkiliKisi || '',
            yetkiliTelefon: techService.yetkiliTelefon || '',
            adres: techService.adres || '',
            cityId: techService.cityId ? String(techService.cityId) : '',
            districtId: techService.districtId ? String(techService.districtId) : '',
            enlem: enlem ? String(enlem) : '',
            boylam: boylam ? String(boylam) : '',
            zoom: zoom,
            sirketBilgileri: sirketBilgileri,
            bankaBilgileri: bankaBilgileri,
            telefon: techService.telefon || '',
            faks: techService.faks || '',
            katilmaTarihi: techService.katilmaTarihi
              ? String(techService.katilmaTarihi).slice(0, 10)
              : '',
            aciklama: techService.aciklama || '',
            aktif: techService.aktif !== undefined ? Boolean(techService.aktif) : true,
            isTest: techService.isTest === true,
          };
          
          console.log('[loadTechService] Loaded state:', {
            cityId: loadedState.cityId,
            districtId: loadedState.districtId,
            techServiceCityId: techService.cityId,
            techServiceDistrictId: techService.districtId,
          });
          
          setFormState(loadedState);
          // İlk yüklenen state'i sakla
          initialFormStateRef.current = JSON.parse(JSON.stringify(loadedState));
          
          // Breadcrumb için teknik servis adını set et
          if (loadedState.ad) {
            window.currentTechServiceName = loadedState.ad;
          }
          
          // Konum varsa harita merkezini ayarla
          if (enlem && boylam) {
            const lat = Number(enlem);
            const lng = Number(boylam);
            setMapCenter({ lat, lng });
            setMapZoom(zoom);
            setPinPosition({ lat, lng });
          }
          
          // Yetkili alanları yükle
          if (id) {
            loadServiceAreas(id);
          }
        }
      } catch (err) {
        console.error('Teknik servis yüklenemedi:', err);
        setTeknikServisKullanicilari([]);
        setFormError(getErrorMessage(err, 'Teknik servis yüklenemedi'));
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Teknik servis yüklenemedi'));
        }
      } finally {
        setLoading(false);
      }
    };
    loadTechService();
  }, [id, currentMode, loadServiceAreas]);

  useEffect(() => {
    if (activeSectionTab !== 'technicians' || !id || currentMode !== 'edit') {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setServiceTabTechniciansLoading(true);
      setServiceTabTechniciansError('');
      try {
        const res = await fetchTechniciansByTechService(id, { aktif: 'all' });
        if (cancelled) return;
        const list = res?.veri?.teknisyenler || [];
        setServiceTabTechnicians(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) {
          setServiceTabTechnicians([]);
          setServiceTabTechniciansError(getErrorMessage(err, 'Teknisyenler yüklenemedi'));
          if (window.showNotification) {
            window.showNotification('error', getErrorMessage(err, 'Teknisyenler yüklenemedi'));
          }
        }
      } finally {
        if (!cancelled) setServiceTabTechniciansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSectionTab, id, currentMode]);

  // Yetkili Alanlar sekmesinde ürün ağacı (tablo detay / ürün kaldırma)
  useEffect(() => {
    if (activeSectionTab !== 'authorized' || currentMode !== 'edit' || !id) return;
    if (authorizedAreaProductCategories.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchCategories({ kategoriTipi: 'urun' });
        if (!cancelled) {
          setAuthorizedAreaProductCategories(normalizeAuthorizedCategoryList(response));
        }
      } catch (err) {
        console.error('Ürün kategorileri yüklenemedi:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSectionTab, currentMode, id, authorizedAreaProductCategories.length]);
  
  // Yetkili alan modal'ını aç
  const openAuthorizedAreaModal = async () => {
    setAuthorizedAreaFormState({
      cityId: '',
      selectedDistricts: [],
      areaSelection: emptySelection(),
    });
    setAuthorizedAreaDistricts([]);
    setDistrictServiceAreaChecks({});
    setIsAuthorizedAreaModalOpen(true);
    try {
      const [response, urunList] = await Promise.all([
        fetchCategories({ kategoriTipi: 'urun' }),
        fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true }),
      ]);
      setAuthorizedAreaProductCategories(normalizeAuthorizedCategoryList(response));
      setAuthorizedAreaUrunContents(Array.isArray(urunList) ? urunList : []);
    } catch (err) {
      console.error('Ürün kategorileri yüklenemedi:', err);
      setAuthorizedAreaProductCategories([]);
    }
  };

  const closeAuthorizedAreaModal = () => {
    setIsAuthorizedAreaModalOpen(false);
    setAuthorizedAreaFormState({
      cityId: '',
      selectedDistricts: [],
      areaSelection: emptySelection(),
    });
    setAuthorizedAreaDistricts([]);
    setDistrictServiceAreaChecks({});
    setAuthorizedAreaProductCategories([]);
    setAuthorizedAreaUrunContents([]);
  };
  
  // İl seçildiğinde ilçeleri yükle ve yetki kontrolü yap
  const handleAuthorizedAreaCityChange = async (cityId) => {
    if (!cityId) {
      setAuthorizedAreaFormState(prev => ({
        ...prev,
        cityId: '',
        selectedDistricts: [],
      }));
      setAuthorizedAreaDistricts([]);
      return;
    }
    
    setAuthorizedAreaDistrictsLoading(true);
    try {
      const districtsResponse = await fetchDistrictsByCity(cityId);
      console.log('[handleAuthorizedAreaCityChange] districtsResponse:', districtsResponse);
      console.log('[handleAuthorizedAreaCityChange] districtsResponse.veri?.ilceler:', districtsResponse?.veri?.ilceler);
      
      // Farklı response formatlarını kontrol et
      let list = districtsResponse?.veri?.ilceler || districtsResponse?.veri?.districts || districtsResponse?.veri || [];
      
      if (!Array.isArray(list)) {
        console.warn('[handleAuthorizedAreaCityChange] Districts list is not an array:', list);
        list = [];
      }
      
      // API formatı: { id: number, adi: string, cityId: number }
      const serializedDistricts = list.map((ilce) => {
        return {
          id: ilce.id || ilce._id,
          adi: ilce.adi || ilce.ad || ilce.name,
          ad: ilce.adi || ilce.ad || ilce.name, // Geriye dönük uyumluluk için
          name: ilce.name || ilce.adi || ilce.ad, // Geriye dönük uyumluluk için
          cityId: ilce.cityId
        };
      });
      
      console.log('[handleAuthorizedAreaCityChange] Serialized districts:', serializedDistricts);
      setAuthorizedAreaDistricts(serializedDistricts);
      
      if (serializedDistricts.length > 0) {
        // Bu teknik servisin bu il için daha önce eklenmiş ilçeleri bul ve seçili yap
        let existingDistricts = [];
        if (id && serviceAreas && Array.isArray(serviceAreas) && serviceAreas.length > 0) {
          existingDistricts = serviceAreas
            .filter(area => {
              // İl ID'sini kontrol et (farklı formatlarda gelebilir)
              const areaCityId = area.cityId || area.il?.id || area.il?._id;
              const cityIdNum = Number(cityId);
              const areaCityIdNum = Number(areaCityId);
              return areaCityIdNum === cityIdNum || String(areaCityId) === String(cityId);
            })
            .map(area => {
              // İlçe ID'sini al (farklı formatlarda gelebilir)
              const districtId = area.districtId || area.ilce?.id || area.ilce?._id;
              return districtId ? String(districtId) : null;
            })
            .filter(Boolean); // null değerleri filtrele
          
          console.log('[handleAuthorizedAreaCityChange] Mevcut ilçeler bulundu:', existingDistricts);
          console.log('[handleAuthorizedAreaCityChange] serviceAreas:', serviceAreas);
          console.log('[handleAuthorizedAreaCityChange] cityId:', cityId);
        }
        
        // State'i güncelle - hem cityId hem de selectedDistricts'i birlikte
        setAuthorizedAreaFormState(prev => ({
          ...prev,
          cityId,
          selectedDistricts: existingDistricts,
        }));
      } else {
        setAuthorizedAreaDistricts([]);
        setDistrictServiceAreaChecks({});
        setAuthorizedAreaFormState(prev => ({
          ...prev,
          cityId,
          selectedDistricts: [],
        }));
      }
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
      setAuthorizedAreaDistricts([]);
      setDistrictServiceAreaChecks({});
      setAuthorizedAreaFormState(prev => ({
        ...prev,
        cityId,
        selectedDistricts: [],
      }));
    } finally {
      setAuthorizedAreaDistrictsLoading(false);
    }
  };
  
  const handleServiceAreaCategoriesUpdated = useCallback((updated) => {
    const uid = String(updated?.id || updated?._id || '');
    if (!uid) return;
    setServiceAreas((prev) =>
      prev.map((a) => {
        const aid = String(a.id || a._id);
        if (aid !== uid) return a;
        return {
          ...a,
          ...updated,
          yetkiliKategoriIds: updated.yetkiliKategoriIds ?? a.yetkiliKategoriIds,
          yetkiliKategoriler: updated.yetkiliKategoriler ?? a.yetkiliKategoriler,
        };
      })
    );
  }, []);

  // Yetki alanını sil
  const handleAuthorizedAreaDelete = async (serviceAreaId, districtName) => {
    const confirmed = await confirm({
      title: 'Yetki alanını kaldır',
      message: `Yetki Alanlarından ${districtName} kaldırılacak. Onaylıyor musunuz?`,
      variant: 'warning',
      confirmLabel: 'Evet',
    });
    if (!confirmed) {
      return;
    }
    
    try {
      await deleteServiceArea(serviceAreaId);
      
      // Yetkili alanları yeniden yükle
      await loadServiceAreas(id);
      
      if (window.showNotification) {
        window.showNotification('success', 'Yetki alanı başarıyla silindi');
      }
    } catch (error) {
      console.error('Yetki alanı silinemedi:', error);
      const errorMessage = error?.message || 'Yetki alanı silinirken bir hata oluştu';
      if (window.showNotification) {
        window.showNotification('error', errorMessage);
      }
    }
  };


  const getDistrictAuthCheck = (districtId) => {
    const key = Number(districtId);
    return (
      districtServiceAreaChecks[key] ||
      districtServiceAreaChecks[districtId] ||
      districtServiceAreaChecks[String(districtId)]
    );
  };

  const handleDistrictCheckboxChange = (districtId, checked) => {
    setAuthorizedAreaFormState((prev) => {
      const selectedDistricts = checked
        ? [...prev.selectedDistricts, districtId]
        : prev.selectedDistricts.filter((id) => id !== districtId);
      return { ...prev, selectedDistricts };
    });
  };

  const authorizedAreaIller = useMemo(() => {
    return [...iller].sort((a, b) =>
      String(a.name || a.ad || '')
        .trim()
        .localeCompare(String(b.name || b.ad || '').trim(), 'tr', { sensitivity: 'base' })
    );
  }, [iller]);

  const authorizedAreaProductsByLeaf = useMemo(
    () => buildProductsByLeaf(authorizedAreaUrunContents),
    [authorizedAreaUrunContents]
  );

  const authorizedAreaSelectionKey = useMemo(
    () =>
      JSON.stringify({
        leaves: [...(authorizedAreaFormState.areaSelection?.leafIds || [])].map(String).sort(),
        products: [...(authorizedAreaFormState.areaSelection?.productIds || [])].map(String).sort(),
      }),
    [authorizedAreaFormState.areaSelection]
  );

  const authorizedAreaDistrictIdsKey = useMemo(
    () =>
      authorizedAreaDistricts
        .map((d) => String(d.id || d._id || ''))
        .filter(Boolean)
        .sort()
        .join(','),
    [authorizedAreaDistricts]
  );

  useEffect(() => {
    if (!isAuthorizedAreaModalOpen || !authorizedAreaFormState.cityId) return;
    if (!authorizedAreaDistricts.length) return;
    const districtNums = authorizedAreaDistricts
      .map((d) => Number(d.id || d._id))
      .filter((n) => !Number.isNaN(n) && n >= 101 && n <= 8199);
    if (!districtNums.length) return;

    let cancelled = false;
    (async () => {
      try {
        const opts = batchCheckOptions(
          authorizedAreaFormState.areaSelection,
          null,
          authorizedAreaProductsByLeaf,
          id && /^[0-9a-fA-F]{24}$/.test(String(id).trim()) ? String(id).trim() : null
        );
        const batchCheckResponse = await checkDistrictsBatch(districtNums, opts);
        if (cancelled) return;
        if (batchCheckResponse?.basarili && batchCheckResponse?.veri?.checks) {
          const checks = {};
          batchCheckResponse.veri.checks.forEach((check) => {
            checks[check.districtId] = {
              yetkili: check.yetkili || false,
              tamYetki: check.tamYetki === true,
              kismiYetki: check.kismiYetki === true,
              techService: check.techService || null,
              serviceAreaId: check.serviceAreaId || null,
            };
          });
          setDistrictServiceAreaChecks(checks);
        } else {
          const checks = {};
          districtNums.forEach((did) => {
            checks[did] = { yetkili: false, techService: null };
          });
          setDistrictServiceAreaChecks(checks);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Toplu ilçe kontrolü başarısız:', error);
        const checks = {};
        districtNums.forEach((did) => {
          checks[did] = { yetkili: false, techService: null };
        });
        setDistrictServiceAreaChecks(checks);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthorizedAreaModalOpen,
    authorizedAreaFormState.cityId,
    authorizedAreaSelectionKey,
    authorizedAreaDistrictIdsKey,
    authorizedAreaProductsByLeaf,
    id,
    districtChecksRefreshKey,
  ]);

  const handleDistrictReassign = async (districtId) => {
    const districtKey = Number(districtId);
    const check = getDistrictAuthCheck(districtId);
    const authorizedService = check?.techService;
    const serviceAreaId = check?.serviceAreaId;

    if (!authorizedService?.ad && !serviceAreaId) {
      if (window.showNotification) {
        window.showNotification('warning', 'Devredilecek yetki kaydı bulunamadı');
      }
      return;
    }

    const servisAd = authorizedService?.ad || 'Diğer servis';
    const confirmed = await confirm({
      title: 'Yetki devri',
      message: `${servisAd} yetkisini bu teknik servise devretmek istiyor musunuz?`,
      variant: 'warning',
      confirmLabel: 'Atama yap',
    });
    if (!confirmed) return;

    if (!id || !authorizedAreaFormState.cityId) {
      if (window.showNotification) {
        window.showNotification('warning', 'İl seçimi gerekli');
      }
      return;
    }

    setReassignLoadingDistrictId(String(districtId));
    try {
      // İlçe satırından devir: karşı servisin o ilçedeki yetkisinin tamamı (modal ürün ağacı yeni ilçe eklerken geçerli)
      await reassignServiceArea({
        targetTechServiceId: id,
        cityId: Number(authorizedAreaFormState.cityId),
        districtId: districtKey,
        serviceAreaId,
      });

      await loadServiceAreas(id);
      setDistrictChecksRefreshKey((k) => k + 1);

      setAuthorizedAreaFormState((prev) => {
        const sid = String(districtId);
        if (prev.selectedDistricts.includes(sid)) return prev;
        return { ...prev, selectedDistricts: [...prev.selectedDistricts, sid] };
      });

      if (window.showNotification) {
        window.showNotification('success', 'Yetki devredildi');
      }
    } catch (error) {
      console.error('Yetki devri başarısız:', error);
      const errorMessage =
        error?.data?.mesaj || error?.response?.data?.mesaj || error?.message || 'Yetki devredilemedi';
      if (window.showNotification) {
        window.showNotification('error', errorMessage);
      }
    } finally {
      setReassignLoadingDistrictId(null);
    }
  };

  // Yetki alanı ekle/güncelle/sil
  const handleAuthorizedAreaSave = async () => {
    if (!authorizedAreaFormState.cityId) {
      if (window.showNotification) {
        window.showNotification('warning', 'Lütfen il seçin');
      }
      return;
    }
    
    if (!id) {
      if (window.showNotification) {
        window.showNotification('error', 'Teknik servis ID bulunamadı');
      }
      return;
    }
    
    // Not: Teknik servis şeması uyumluluk kontrolü kaldırıldı
    // Bir teknik servis, kendi bulunduğu ilden başka illerde ve ilçelerde de yetkili olabilir
    
    try {
      const cityId = Number(authorizedAreaFormState.cityId);
      const selectedDistrictIds = authorizedAreaFormState.selectedDistricts.map(id => Number(id));
      
      // Bu il için daha önce eklenmiş ilçeleri bul
      const existingAreas = serviceAreas.filter(area => {
        const areaCityId = Number(area.cityId || area.il?.id || area.il?._id);
        return areaCityId === cityId;
      });
      
      const existingDistrictIds = existingAreas.map(area => {
        const districtId = Number(area.districtId || area.ilce?.id || area.ilce?._id);
        return districtId;
      }).filter(Boolean);
      
      // Silinecek ilçeler: Daha önce seçili olan ama şimdi seçili olmayanlar
      const districtsToDelete = existingDistrictIds.filter(id => !selectedDistrictIds.includes(id));
      
      // Eklenecek ilçeler: Şimdi seçili olan ama daha önce seçili olmayanlar
      const districtsToAdd = selectedDistrictIds.filter(id => !existingDistrictIds.includes(id));

      const districtsToReassign = [];
      const districtsToCreate = [];
      for (const districtId of districtsToAdd) {
        const check = getDistrictAuthCheck(districtId);
        const holderId = check?.techService?.id;
        if (check?.yetkili && holderId && String(holderId) !== String(id)) {
          districtsToReassign.push({
            districtId,
            serviceAreaId: check.serviceAreaId,
            servisAd: check.techService?.ad || 'Diğer servis',
          });
        } else {
          districtsToCreate.push(districtId);
        }
      }

      if (districtsToReassign.length > 0) {
        const ozet = districtsToReassign.map((r) => r.servisAd).join(', ');
        const devretOnay = await confirm({
          title: 'Yetki devri',
          message: `${districtsToReassign.length} ilçede başka servis yetkisi var (${ozet}). Bu teknik servise devredilsin mi?`,
          variant: 'warning',
          confirmLabel: 'Devret',
        });
        if (!devretOnay) return;
      }
      
      console.log('[handleAuthorizedAreaSave] Eklenecek ilçeler:', districtsToCreate);
      console.log('[handleAuthorizedAreaSave] Devredilecek ilçeler:', districtsToReassign);
      console.log('[handleAuthorizedAreaSave] Silinecek ilçeler:', districtsToDelete);
      
      // Silme işlemleri
      const deletePromises = districtsToDelete.map(districtId => {
        // Bu ilçeye ait yetki alanını bul
        const areaToDelete = existingAreas.find(area => {
          const areaDistrictId = Number(area.districtId || area.ilce?.id || area.ilce?._id);
          return areaDistrictId === districtId;
        });
        
        if (areaToDelete && areaToDelete._id) {
          return deleteServiceArea(areaToDelete._id);
        }
        return Promise.resolve();
      });
      
      await Promise.all(deletePromises);

      for (const row of districtsToReassign) {
        await reassignServiceArea({
          targetTechServiceId: id,
          cityId,
          districtId: Number(row.districtId),
          serviceAreaId: row.serviceAreaId,
        });
      }
      
      if (districtsToCreate.length > 0) {
        const districts = districtsToCreate.map((districtId) => ({
          cityId,
          districtId,
        }));
        const scope = toApiPayload(
          authorizedAreaFormState.areaSelection,
          null,
          authorizedAreaProductsByLeaf
        );
        await createMultipleServiceAreas(id, districts, scope);
      }
      
      // Yetkili alanları yeniden yükle
      await loadServiceAreas(id);
      
      closeAuthorizedAreaModal();
      
      const messages = [];
      if (districtsToReassign.length > 0) {
        messages.push(`${districtsToReassign.length} yetki devredildi`);
      }
      if (districtsToCreate.length > 0) {
        messages.push(`${districtsToCreate.length} yetki alanı eklendi`);
      }
      if (districtsToDelete.length > 0) {
        messages.push(`${districtsToDelete.length} yetki alanı silindi`);
      }
      
      if (window.showNotification) {
        if (messages.length > 0) {
          window.showNotification('success', messages.join(', '));
        } else {
          window.showNotification('info', 'Değişiklik yapılmadı');
        }
      }
    } catch (error) {
      console.error('Yetki alanı işlemi başarısız:', error);
      console.error('Error object:', JSON.stringify(error, null, 2));
      
      // API'den gelen hata mesajını al
      let errorMessage = 'Yetki alanı işlemi sırasında bir hata oluştu';
      
      // Farklı hata formatlarını kontrol et
      if (error?.data?.mesaj) {
        errorMessage = error.data.mesaj;
      } else if (error?.response?.data?.mesaj) {
        errorMessage = error.response.data.mesaj;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      if (window.showNotification) {
        window.showNotification('error', errorMessage);
      }
    }
  };


  // Telefon numarasını temizle ve formatla (10 haneli, başında 0 veya +90 olmadan)
  const formatPhoneNumber = useCallback((value) => {
    if (!value) return '';
    
    // Sadece rakamları al
    let cleaned = String(value).replace(/\D/g, '');
    
    // Başında +90 varsa kaldır (90'ı kaldır)
    if (cleaned.startsWith('90') && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }
    
    // Başında 0 varsa kaldır
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    
    // Maksimum 10 karakter
    if (cleaned.length > 10) {
      cleaned = cleaned.substring(0, 10);
    }
    
    return cleaned;
  }, []);

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    console.log('[TechServiceFormPage] handleFormChange:', { name, value, type });
    
    setFormState((prev) => {
      const newState = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      
      // Telefon numarası alanları için format kontrolü
      if (name === 'telefon' || name === 'yetkiliTelefon') {
        const formatted = formatPhoneNumber(value);
        newState[name] = formatted;
      }
      
      // İl değiştiğinde ilçeyi temizle (artık select'in onChange'inde yapılıyor)
      if (name === 'cityId') {
        newState.districtId = '';
      }
      
      // Debug: districtId değişikliğini logla
      if (name === 'districtId') {
        console.log('[handleFormChange] districtId değişiyor:', {
          oldValue: prev.districtId,
          newValue: value,
          valueType: typeof value
        });
      }
      
      return newState;
    });
  };

  const sectionSuccessMessages = {
    service: 'Servis bilgileri kaydedildi',
    location: 'Lokasyon bilgileri kaydedildi',
    company: 'Şirket bilgileri kaydedildi',
    bank: 'Banka bilgileri kaydedildi',
    other: 'Diğer bilgiler kaydedildi',
  };

  const validateSection = (sectionKey) => {
    const needsService = !sectionKey || sectionKey === 'service';
    if (needsService) {
      if (!formState.ad.trim()) {
        return 'Teknik servis adı gereklidir';
      }

      if (formState.ad.trim().length < 2 || formState.ad.trim().length > 100) {
        return 'Teknik servis adı 2-100 karakter arasında olmalıdır';
      }

      if (!formState.cityId || formState.cityId === '') {
        return 'İl seçimi gereklidir';
      }

      if (!formState.districtId || formState.districtId === '') {
        return 'İlçe seçimi gereklidir';
      }
      
      // Telefon numarası validasyonu (10 haneli, başında 0 veya +90 olmadan)
      if (formState.telefon && formState.telefon.trim() !== '') {
        const phoneClean = formatPhoneNumber(formState.telefon);
        if (phoneClean.length !== 10) {
          return 'Telefon numarası 10 haneli olmalıdır (başında 0 veya +90 olmadan, örn: 5439553228)';
        }
      }
      
      // Yetkili telefon validasyonu (10 haneli, başında 0 veya +90 olmadan)
      if (formState.yetkiliTelefon && formState.yetkiliTelefon.trim() !== '') {
        const phoneClean = formatPhoneNumber(formState.yetkiliTelefon);
        if (phoneClean.length !== 10) {
          return 'Yetkili telefon numarası 10 haneli olmalıdır (başında 0 veya +90 olmadan, örn: 5439553228)';
        }
      }

      if (formState.faks && formState.faks.trim().length > 40) {
        return 'Faks en fazla 40 karakter olabilir';
      }
    }

    return null;
  };

  const submitForm = async ({ sectionKey, stayOnPage = true, customFormState = null } = {}) => {
    // Eğer customFormState verilmişse onu kullan, yoksa mevcut formState'i kullan
    const stateToUse = customFormState || formState;
    
    console.log('=== submitForm - State Kontrolü ===');
    console.log('customFormState var mı?', customFormState !== null);
    console.log('stateToUse.sirketBilgileri:', JSON.stringify(stateToUse.sirketBilgileri, null, 2));
    console.log('stateToUse.sirketBilgileri.length:', stateToUse.sirketBilgileri?.length || 0);
    console.log('==================================');
    
    const validationMessage = validateSection(sectionKey);
    if (validationMessage) {
      setFormError(validationMessage);
        return;
      }

    const normalizedName = stateToUse.ad.trim();
    if (normalizedName) {
      const duplicate = await checkDuplicateName(normalizedName, currentMode === 'edit' ? stateToUse.id : null);
      if (duplicate) {
        const locationLabel = duplicate?.il?.name || duplicate?.cityId || 'ilgili şehir';
        setFormError(
          `Bu teknik servis adı zaten kayıtlı (${locationLabel}). Öneri: örneğin "${normalizedName} ${locationLabel}" gibi farklı bir adla tekrar deneyin.`
        );
        return;
      }
    }

    setFormSubmitting(true);
    setFormError('');

    try {
      // NOT: Frontend'de trim/sanitize yapmıyoruz, backend yapacak
      const enlemValue = stateToUse.enlem ? parseFloat(stateToUse.enlem) : null;
      const boylamValue = stateToUse.boylam ? parseFloat(stateToUse.boylam) : null;
      const zoomValue = stateToUse.zoom !== undefined ? parseInt(stateToUse.zoom, 10) : mapZoom || 15;
      // Şirket bilgilerini sadece company sekmesi için gönder
      // NOT: Frontend'de sanitize/validasyon yapmıyoruz, backend yapacak
      let sirketBilgileri = [];
      if (sectionKey === 'company') {
        // Ham veriyi direkt gönder (backend normalize, sanitize ve validate edecek)
        sirketBilgileri = (stateToUse.sirketBilgileri || []).map((sirket, index) => {
          const sirketData = {
            sirketTipi: sirket.sirketTipi || '', // Ham veri, backend normalize edecek
            sirketAdi: sirket.sirketAdi || '', // Ham veri, backend sanitize edecek
            vergiDairesi: sirket.vergiDairesi || '', // Ham veri, backend sanitize edecek
            vergiNumarasi: sirket.vergiNumarasi || '', // Ham veri, backend validate edecek
            sirketAdresi: sirket.sirketAdresi || '', // Ham veri, backend sanitize edecek
          };
          
          // Debug: Her şirket bilgisini logla
          console.log(`[submitForm] Şirket ${index} (ham veri):`, {
            sirketTipi: sirketData.sirketTipi,
            sirketTipiType: typeof sirketData.sirketTipi,
            sirketTipiLength: sirketData.sirketTipi.length,
            sirketTipiCharCodes: sirketData.sirketTipi.split('').map(c => c.charCodeAt(0)),
            fullData: sirketData,
          });
          
          return sirketData;
        });
        
        console.log('[submitForm] Tüm şirket bilgileri (ham veri, backend işleyecek):', JSON.stringify(sirketBilgileri, null, 2));
      }
      
      console.log('=== submitForm - Şirket Bilgileri İşleme ===');
      console.log('stateToUse.sirketBilgileri (ham):', JSON.stringify(stateToUse.sirketBilgileri, null, 2));
      console.log('stateToUse.sirketBilgileri.length:', stateToUse.sirketBilgileri?.length || 0);
      console.log('stateToUse.sirketBilgileri type:', Array.isArray(stateToUse.sirketBilgileri) ? 'Array' : typeof stateToUse.sirketBilgileri);
      console.log('sirketBilgileri (işlenmiş):', JSON.stringify(sirketBilgileri, null, 2));
      console.log('sirketBilgileri.length:', sirketBilgileri.length);
      console.log('sirketBilgileri type:', Array.isArray(sirketBilgileri) ? 'Array' : typeof sirketBilgileri);
      console.log('customFormState kullanılıyor mu?', customFormState !== null);
      if (customFormState) {
        console.log('customFormState.sirketBilgileri:', JSON.stringify(customFormState.sirketBilgileri, null, 2));
      }
      console.log('==========================================');

      const bankaBilgileri = (stateToUse.bankaBilgileri || []).map(banka => ({
        bankaId: banka.bankaId || undefined,
        hesapAdi: banka.hesapAdi ? banka.hesapAdi.trim() : undefined,
        iban: banka.iban ? banka.iban.trim().replace(/\s/g, '').toUpperCase() : undefined,
      })).filter(banka => banka.bankaId && banka.hesapAdi && banka.iban);
      
      // NOT: Frontend'de sanitize/validasyon yapmıyoruz, backend yapacak
      // sectionKey'e göre sadece ilgili alanları gönder
      const payload = {};
      
      // Service sekmesi için tüm temel bilgiler (ham veri, backend işleyecek)
      if (!sectionKey || sectionKey === 'service') {
        payload.ad = stateToUse.ad || '';
        payload.adres = stateToUse.adres || '';
        payload.telefon = stateToUse.telefon ? formatPhoneNumber(stateToUse.telefon) : '';
        payload.mail = stateToUse.mail ? String(stateToUse.mail).toLowerCase() : '';
        payload.yetkiliKisi = stateToUse.yetkiliKisi || '';
        payload.yetkiliTelefon = stateToUse.yetkiliTelefon ? formatPhoneNumber(stateToUse.yetkiliTelefon) : '';
        if (stateToUse.faks !== undefined && stateToUse.faks !== null) {
          payload.faks = stateToUse.faks.trim();
        }
        if (stateToUse.katilmaTarihi && String(stateToUse.katilmaTarihi).trim() !== '') {
          payload.katilmaTarihi = String(stateToUse.katilmaTarihi).trim();
        }
        payload.cityId = stateToUse.cityId ? parseInt(stateToUse.cityId, 10) : undefined;
        payload.districtId = stateToUse.districtId ? parseInt(stateToUse.districtId, 10) : undefined;
      }
      
      // Location sekmesi için sadece konum bilgileri
      if (sectionKey === 'location') {
        // cityId ve districtId'yi gönder (eğer varsa)
        if (stateToUse.cityId) {
          payload.cityId = parseInt(stateToUse.cityId, 10);
        }
        if (stateToUse.districtId) {
          payload.districtId = parseInt(stateToUse.districtId, 10);
        }
        
        console.log('[submitForm] Location sekmesi payload:', {
          sectionKey,
          cityId: stateToUse.cityId,
          districtId: stateToUse.districtId,
          enlem: enlemValue,
          boylam: boylamValue,
          payload: JSON.stringify(payload, null, 2),
        });
      }
      
      // Konum bilgisi (hem service hem location için)
      if (
        (!sectionKey || sectionKey === 'service' || sectionKey === 'location') &&
        enlemValue !== null &&
        boylamValue !== null &&
        Number.isFinite(enlemValue) &&
        Number.isFinite(boylamValue)
      ) {
        payload.konum = {
          koordinat: {
            enlem: enlemValue,
            boylam: boylamValue,
          },
          zoom: Math.max(0, Math.min(20, zoomValue)),
        };
      }

      // Şirket bilgileri sadece company sekmesi için
      if (sectionKey === 'company') {
        payload.sirketBilgileri = Array.isArray(sirketBilgileri) ? sirketBilgileri : [];
      }

      // Banka bilgileri sadece bank sekmesi için
      if (sectionKey === 'bank') {
        if (bankaBilgileri.length > 0) {
          payload.bankaBilgileri = bankaBilgileri;
        }
      }

      // Other sekmesi için (ham veri, backend işleyecek)
      if (sectionKey === 'other') {
        payload.aciklama = stateToUse.aciklama || '';
        payload.aktif = stateToUse.aktif !== undefined ? Boolean(stateToUse.aktif) : true;
        payload.isTest = stateToUse.isTest === true;
      }

      // Console'a gönderilen payload'ı yazdır
      console.log('=== API PAYLOAD (Şirket Bilgileri) ===');
      console.log('Section Key:', sectionKey);
      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('Şirket Bilgileri:', JSON.stringify(sirketBilgileri, null, 2));
      console.log('=====================================');

      let response;
      if (currentMode === 'create') {
        response = await createTechService(payload);
        
        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Teknik servis oluşturulamadı');
        }
        
        const createdRecord = response?.veri?.teknikServis || response?.veri || {};
        const newId = createdRecord?.id || createdRecord?._id || createdRecord?.techServiceId || stateToUse.id;
        const newKod =
          createdRecord?.kod != null && createdRecord.kod !== '' ? String(createdRecord.kod) : '';
        if (newId) {
          servicesRef.current = [...servicesRef.current, { ...createdRecord, id: newId }];
        }
        if (newId) {
          setFormState((prev) => ({ ...prev, id: newId, ...(newKod ? { kod: newKod } : {}) }));
        }
        invalidateTechServicesSelectCache();
        setCurrentMode('edit');
        // Başarılı kayıt sonrası initial state'i güncelle
        initialFormStateRef.current = JSON.parse(
          JSON.stringify({ ...stateToUse, id: newId, ...(newKod ? { kod: newKod } : {}) })
        );
        
        if (window.showNotification) {
          window.showNotification('success', sectionSuccessMessages[sectionKey] || 'Teknik servis başarıyla oluşturuldu');
        }
      } else {
        if (!stateToUse.id) {
          throw new Error('Geçersiz teknik servis');
        }
        response = await updateTechService(stateToUse.id, payload);
        
        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Teknik servis güncellenemedi');
        }
        
        invalidateTechServicesSelectCache();
        // Başarılı kayıt sonrası initial state'i güncelle
        initialFormStateRef.current = JSON.parse(JSON.stringify(stateToUse));
        
        if (window.showNotification) {
          window.showNotification('success', sectionSuccessMessages[sectionKey] || 'Teknik servis bilgileri güncellendi');
        }
      }

      if (!stayOnPage) {
      navigate('/tech-services');
      }
      return true;
    } catch (submitError) {
      console.error('Form submit error:', submitError);
      const errorMessage = getErrorMessage(submitError, 'İşlem gerçekleştirilemedi');
      setFormError(errorMessage);
      if (window.showNotification) {
        window.showNotification('error', errorMessage);
      }
    } finally {
      setFormSubmitting(false);
    }
    return false;
  };

  // Her sekme için değişiklik kontrolü
  const hasSectionChanges = useCallback((sectionKey) => {
    if (!initialFormStateRef.current) {
      // Create modunda veya henüz yüklenmemişse her zaman enabled
      return true;
    }

    const initial = initialFormStateRef.current;
    const current = formState;

    switch (sectionKey) {
      case 'service':
        return (
          (initial.ad || '') !== (current.ad || '') ||
          (initial.adres || '') !== (current.adres || '') ||
          (initial.telefon || '') !== (current.telefon || '') ||
          (initial.mail || '') !== (current.mail || '') ||
          (initial.yetkiliKisi || '') !== (current.yetkiliKisi || '') ||
          (initial.yetkiliTelefon || '') !== (current.yetkiliTelefon || '') ||
          (initial.faks || '') !== (current.faks || '') ||
          (initial.katilmaTarihi || '') !== (current.katilmaTarihi || '') ||
          (initial.cityId || '') !== (current.cityId || '') ||
          (initial.districtId || '') !== (current.districtId || '')
        );
      case 'location':
        return (
          (initial.cityId || '') !== (current.cityId || '') ||
          (initial.districtId || '') !== (current.districtId || '') ||
          (initial.enlem || '') !== (current.enlem || '') ||
          (initial.boylam || '') !== (current.boylam || '') ||
          (initial.zoom || 15) !== (current.zoom || 15)
        );
      case 'company':
        const initialCompanies = JSON.stringify((initial.sirketBilgileri || []).sort((a, b) => {
          const aKey = `${a.sirketTipi || ''}_${a.sirketAdi || ''}`;
          const bKey = `${b.sirketTipi || ''}_${b.sirketAdi || ''}`;
          return aKey.localeCompare(bKey);
        }));
        const currentCompanies = JSON.stringify((current.sirketBilgileri || []).sort((a, b) => {
          const aKey = `${a.sirketTipi || ''}_${a.sirketAdi || ''}`;
          const bKey = `${b.sirketTipi || ''}_${b.sirketAdi || ''}`;
          return aKey.localeCompare(bKey);
        }));
        return initialCompanies !== currentCompanies;
      case 'bank':
        const initialBanks = JSON.stringify((initial.bankaBilgileri || []).sort((a, b) => {
          const aStr = `${a.bankaId || ''}-${a.hesapAdi || ''}-${a.iban || ''}`;
          const bStr = `${b.bankaId || ''}-${b.hesapAdi || ''}-${b.iban || ''}`;
          return aStr.localeCompare(bStr);
        }));
        const currentBanks = JSON.stringify((current.bankaBilgileri || []).sort((a, b) => {
          const aStr = `${a.bankaId || ''}-${a.hesapAdi || ''}-${a.iban || ''}`;
          const bStr = `${b.bankaId || ''}-${b.hesapAdi || ''}-${b.iban || ''}`;
          return aStr.localeCompare(bStr);
        }));
        return initialBanks !== currentBanks;
      case 'other':
        return (
          (initial.aciklama || '') !== (current.aciklama || '') ||
          (initial.aktif !== undefined ? Boolean(initial.aktif) : true) !== (current.aktif !== undefined ? Boolean(current.aktif) : true) ||
          Boolean(initial.isTest) !== Boolean(current.isTest)
        );
      default:
        return false;
    }
  }, [formState]);

  const handleSectionSave = (sectionKey) => async () => {
    const success = await submitForm({ sectionKey, stayOnPage: true });
    if (success) {
      // Başarılı kayıt sonrası initial state'i güncelle
      initialFormStateRef.current = JSON.parse(JSON.stringify(formState));
      
      const message = `${TECH_SERVICE_TABS.find((tab) => tab.key === sectionKey)?.label || 'Sekme'} kaydedildi.`;
      if (window.showNotification) {
        window.showNotification('success', message);
      }
    } else {
      if (window.showNotification) {
        window.showNotification('error', 'Kaydetme sırasında bir sorun oluştu. Konsolu kontrol edin.');
      }
    }
  };

  const openLocationSelector = () => {
    const currentLat = formState.enlem ? parseFloat(formState.enlem) : null;
    const currentLng = formState.boylam ? parseFloat(formState.boylam) : null;
    const currentZoom = formState.zoom !== undefined ? parseInt(formState.zoom, 10) : null;

    setOriginalLocation({
      lat: currentLat,
      lng: currentLng,
      zoom: currentZoom,
    });

    if (currentLat !== null && currentLng !== null && !Number.isNaN(currentLat) && !Number.isNaN(currentLng)) {
      const lat = Number(currentLat);
      const lng = Number(currentLng);
      const zoom = currentZoom !== null && currentZoom !== undefined ? currentZoom : 15;

      setMapCenter({ lat, lng });
      setMapZoom(zoom);
      setPinPosition({ lat, lng });
      setSelectedLocation({ lat, lng });
      setPinVisible(true);
    } else {
      if (formState.cityId && iller.length > 0) {
        setMapCenter({ lat: 39.9334, lng: 32.8597 });
        setMapZoom(6);
      }
      setPinPosition({ lat: null, lng: null });
      setSelectedLocation({ lat: null, lng: null });
      setPinVisible(false);
    }

    setIsLocationModalOpen(true);
  };

  // Şirket bilgileri modal fonksiyonları
  const openCompanyModal = (mode = 'create', index = null) => {
    setCompanyModalMode(mode);
    setEditingCompanyIndex(index);
    if (mode === 'edit' && index !== null && formState.sirketBilgileri[index]) {
      const sirket = formState.sirketBilgileri[index];
      setCompanyFormState({
        sirketTipi: sirket.sirketTipi || '',
        sirketAdi: sirket.sirketAdi || '',
        vergiDairesi: sirket.vergiDairesi || '',
        vergiNumarasi: sirket.vergiNumarasi || '',
        sirketAdresi: sirket.sirketAdresi || '',
      });
    } else {
      setCompanyFormState({
        sirketTipi: '',
        sirketAdi: '',
        vergiDairesi: '',
        vergiNumarasi: '',
        sirketAdresi: '',
      });
    }
    setIsCompanyModalOpen(true);
  };

  const closeCompanyModal = () => {
    setIsCompanyModalOpen(false);
    setCompanyModalMode('create');
    setEditingCompanyIndex(null);
    setCompanyFormState({
      sirketTipi: '',
      sirketAdi: '',
      vergiDairesi: '',
      vergiNumarasi: '',
      sirketAdresi: '',
    });
  };

  const handleCompanyFormChange = (event) => {
    const { name, value } = event.target;
    let processedValue = value;
    
    // Vergi numarası için sadece rakam girişine izin ver
    if (name === 'vergiNumarasi') {
      processedValue = value.replace(/\D/g, '');
    }
    // Diğer alanlar için sanitize yapma - kullanıcı normal yazabilsin
    // XSS koruması sadece API'ye gönderirken yapılacak
    
    setCompanyFormState(prev => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleCompanySave = async () => {
    // Validasyon - Tüm alanlar zorunlu
    if (!companyFormState.sirketTipi || companyFormState.sirketTipi.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Lütfen şirket tipi seçin');
      }
      return;
    }

    if (!companyFormState.sirketAdi || companyFormState.sirketAdi.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Şirket adı gereklidir');
      }
      return;
    }

    if (!companyFormState.vergiDairesi || companyFormState.vergiDairesi.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Vergi dairesi gereklidir');
      }
      return;
    }

    if (!companyFormState.vergiNumarasi || companyFormState.vergiNumarasi.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Vergi numarası gereklidir');
      }
      return;
    }

    // Vergi numarası validasyonu: sadece rakam, 10 veya 11 haneli
    const vergiNumarasiClean = companyFormState.vergiNumarasi.trim().replace(/\s/g, '');
    const vergiNumarasiRegex = /^\d{10,11}$/;
    if (!vergiNumarasiRegex.test(vergiNumarasiClean)) {
      if (window.showNotification) {
        window.showNotification('warning', 'Vergi numarası sadece rakamlardan oluşmalı ve 10 veya 11 haneli olmalıdır');
      }
      return;
    }

    if (!companyFormState.sirketAdresi || companyFormState.sirketAdresi.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Şirket adresi gereklidir');
      }
      return;
    }

    // Şirket bilgisini kaydet
    // NOT: Ham veriyi kaydediyoruz, backend normalize/sanitize edecek
    const newSirketBilgileri = [...(formState.sirketBilgileri || [])];
    
    // Vergi numarasını temizle (sadece rakam) - UX için
    const cleanVergiNumarasi = companyFormState.vergiNumarasi ? companyFormState.vergiNumarasi.trim().replace(/\D/g, '') : '';
    
    // sirketTipi değerini al (ham veri, backend normalize edecek)
    // Select input'tan gelen değer direkt kullanılacak
    const sirketTipiValue = companyFormState.sirketTipi || '';
    
    console.log('[handleCompanySave] Şirket bilgisi kaydediliyor:', {
      sirketTipi: sirketTipiValue,
      sirketTipiType: typeof sirketTipiValue,
      sirketTipiLength: sirketTipiValue.length,
      sirketTipiCharCodes: sirketTipiValue.split('').map(c => c.charCodeAt(0)),
      companyFormState: companyFormState,
    });
    
    // Şirket bilgisi objesi (ham veri, backend işleyecek)
    const sirketBilgisi = {
      sirketTipi: sirketTipiValue, // Ham veri, backend normalize edecek
      sirketAdi: companyFormState.sirketAdi || '', // Ham veri, backend sanitize edecek
      vergiDairesi: companyFormState.vergiDairesi || '', // Ham veri, backend sanitize edecek
      vergiNumarasi: cleanVergiNumarasi, // Sadece rakam, backend validate edecek
      sirketAdresi: companyFormState.sirketAdresi || '', // Ham veri, backend sanitize edecek
    };
    
    if (companyModalMode === 'edit' && editingCompanyIndex !== null) {
      newSirketBilgileri[editingCompanyIndex] = sirketBilgisi;
    } else {
      newSirketBilgileri.push(sirketBilgisi);
    }

    // State'i güncelle
    const updatedFormState = {
      ...formState,
      sirketBilgileri: newSirketBilgileri,
    };
    
    console.log('=== handleCompanySave - Şirket Bilgileri ===');
    console.log('newSirketBilgileri:', JSON.stringify(newSirketBilgileri, null, 2));
    console.log('updatedFormState.sirketBilgileri:', JSON.stringify(updatedFormState.sirketBilgileri, null, 2));
    console.log('==========================================');
    
    setFormState(updatedFormState);
    closeCompanyModal();
    
    // API'ye kaydet - güncellenmiş state'i direkt parametre olarak geçir
    try {
      const success = await submitForm({ 
        sectionKey: 'company', 
        stayOnPage: true,
        customFormState: updatedFormState 
      });
      
      if (success) {
        // Başarılı kayıt sonrası initial state'i güncelle
        initialFormStateRef.current = JSON.parse(JSON.stringify(updatedFormState));
        if (window.showNotification) {
          window.showNotification('success', companyModalMode === 'edit' ? 'Şirket bilgisi güncellendi' : 'Şirket bilgisi eklendi');
        }
      } else {
        // Hata durumunda eski state'e geri dön
        setFormState(formState);
        if (window.showNotification) {
          window.showNotification('error', 'Kaydetme sırasında bir sorun oluştu. Konsolu kontrol edin.');
        }
      }
    } catch (error) {
      console.error('Şirket bilgisi kaydedilemedi:', error);
      // Hata durumunda eski state'e geri dön
      setFormState(formState);
      if (window.showNotification) {
        window.showNotification('error', 'Kaydetme sırasında bir sorun oluştu. Konsolu kontrol edin.');
      }
    }
  };

  const handleCompanyDelete = async (index) => {
    const sirket = formState.sirketBilgileri[index];
    const sirketAdi = sirket?.sirketAdi || 'bu şirket bilgisi';
    const confirmationMessage = `${sirketAdi} adlı şirket bilgisini silmek istediğinize emin misiniz?`;
    
    const confirmed = await confirm({
      title: 'Şirket bilgisini sil',
      message: confirmationMessage,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    
    if (!confirmed) {
      return;
    }

    const newSirketBilgileri = formState.sirketBilgileri.filter((_, i) => i !== index);
    
    // State'i güncelle
    const updatedFormState = {
      ...formState,
      sirketBilgileri: newSirketBilgileri,
    };
    
    setFormState(updatedFormState);
    
    // API'ye kaydet - güncellenmiş state'i direkt parametre olarak geçir
    try {
      const success = await submitForm({ 
        sectionKey: 'company', 
        stayOnPage: true,
        customFormState: updatedFormState 
      });
      
      if (success) {
        // Başarılı kayıt sonrası initial state'i güncelle
        initialFormStateRef.current = JSON.parse(JSON.stringify(updatedFormState));
        if (window.showNotification) {
          window.showNotification('success', 'Şirket bilgisi silindi');
        }
      } else {
        // Hata durumunda eski state'e geri dön
        setFormState(formState); // Revert to previous state
        if (window.showNotification) {
          window.showNotification('error', 'Silme sırasında bir sorun oluştu. Konsolu kontrol edin.');
        }
      }
    } catch (error) {
      console.error('Şirket bilgisi silinemedi:', error);
      // Hata durumunda eski state'e geri dön
      setFormState(formState); // Revert to previous state
      if (window.showNotification) {
        window.showNotification('error', 'Silme sırasında bir sorun oluştu. Konsolu kontrol edin.');
      }
    }
  };

  // Banka bilgileri modal fonksiyonları
  const openBankModal = (mode = 'create', index = null) => {
    setBankModalMode(mode);
    setEditingBankIndex(index);
    if (mode === 'edit' && index !== null && formState.bankaBilgileri[index]) {
      const banka = formState.bankaBilgileri[index];
      setBankFormState({
        bankaId: banka.bankaId || '',
        hesapAdi: banka.hesapAdi || '',
        iban: banka.iban || '',
      });
    } else {
      setBankFormState({ bankaId: '', hesapAdi: '', iban: '' });
    }
    setIsBankModalOpen(true);
  };

  const closeBankModal = () => {
    setIsBankModalOpen(false);
    setBankModalMode('create');
    setEditingBankIndex(null);
    setBankFormState({ bankaId: '', hesapAdi: '', iban: '' });
  };

  const handleBankFormChange = (event) => {
    const { name, value } = event.target;
    setBankFormState(prev => ({
      ...prev,
      [name]: name === 'iban' ? value.toUpperCase().replace(/\s/g, '') : value,
    }));
  };

  const handleBankSave = async () => {
    // Validasyon
    if (!bankFormState.bankaId || bankFormState.bankaId === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Lütfen bir banka seçin');
      }
      return;
    }

    if (!bankFormState.hesapAdi || bankFormState.hesapAdi.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'Hesap adı gereklidir');
      }
      return;
    }

    if (!bankFormState.iban || bankFormState.iban.trim() === '') {
      if (window.showNotification) {
        window.showNotification('warning', 'IBAN gereklidir');
      }
      return;
    }

    // IBAN formatı kontrolü
    const ibanRegex = /^TR[0-9]{24}$/;
    if (!ibanRegex.test(bankFormState.iban.trim())) {
      if (window.showNotification) {
        window.showNotification('warning', 'IBAN formatı geçersiz (TR ile başlayan 26 karakter olmalıdır)');
      }
      return;
    }

    // Banka bilgisini kaydet
    const newBankaBilgileri = [...(formState.bankaBilgileri || [])];
    
    if (bankModalMode === 'edit' && editingBankIndex !== null) {
      newBankaBilgileri[editingBankIndex] = {
        bankaId: bankFormState.bankaId,
        hesapAdi: bankFormState.hesapAdi.trim(),
        iban: bankFormState.iban.trim().toUpperCase(),
      };
    } else {
      newBankaBilgileri.push({
        bankaId: bankFormState.bankaId,
        hesapAdi: bankFormState.hesapAdi.trim(),
        iban: bankFormState.iban.trim().toUpperCase(),
      });
    }

    // State'i güncelle
    const updatedFormState = {
      ...formState,
      bankaBilgileri: newBankaBilgileri,
    };

    setFormState(updatedFormState);
    closeBankModal();
    
    // API'ye kaydet - güncellenmiş state'i direkt parametre olarak geçir
    try {
      const success = await submitForm({ 
        sectionKey: 'bank', 
        stayOnPage: true,
        customFormState: updatedFormState 
      });
      
      if (success) {
        // Başarılı kayıt sonrası initial state'i güncelle
        initialFormStateRef.current = JSON.parse(JSON.stringify(updatedFormState));
        if (window.showNotification) {
          window.showNotification('success', bankModalMode === 'edit' ? 'Banka bilgisi güncellendi' : 'Banka bilgisi eklendi');
        }
      } else {
        // Hata durumunda eski state'e geri dön
        setFormState(formState); // Revert to previous state
        if (window.showNotification) {
          window.showNotification('error', 'Kaydetme sırasında bir sorun oluştu. Konsolu kontrol edin.');
        }
      }
    } catch (error) {
      console.error('Banka bilgisi kaydedilemedi:', error);
      // Hata durumunda eski state'e geri dön
      setFormState(formState); // Revert to previous state
      if (window.showNotification) {
        window.showNotification('error', 'Kaydetme sırasında bir sorun oluştu. Konsolu kontrol edin.');
      }
    }
  };

  const handleBankDelete = async (index) => {
    const banka = formState.bankaBilgileri[index];
    const bankaAdi = banka?.hesapAdi || 'bu banka bilgisi';
    const confirmationMessage = `${bankaAdi} için banka kaydını silmek istediğinize emin misiniz?`;
    const confirmed = await confirm({
      title: 'Banka bilgisini sil',
      message: confirmationMessage,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    
    if (!confirmed) {
      return;
    }

    const newBankaBilgileri = formState.bankaBilgileri.filter((_, i) => i !== index);
    
    // State'i güncelle
    const updatedFormState = {
      ...formState,
      bankaBilgileri: newBankaBilgileri,
    };
    
    setFormState(updatedFormState);
    
    // API'ye kaydet - güncellenmiş state'i direkt parametre olarak geçir
    try {
      const success = await submitForm({ 
        sectionKey: 'bank', 
        stayOnPage: true,
        customFormState: updatedFormState 
      });
      
      if (success) {
        // Başarılı kayıt sonrası initial state'i güncelle
        initialFormStateRef.current = JSON.parse(JSON.stringify(updatedFormState));
        if (window.showNotification) {
          window.showNotification('success', 'Banka bilgisi silindi');
        }
      } else {
        // Hata durumunda eski state'e geri dön
        setFormState(formState); // Revert to previous state
        if (window.showNotification) {
          window.showNotification('error', 'Silme sırasında bir sorun oluştu. Konsolu kontrol edin.');
        }
      }
    } catch (error) {
      console.error('Banka bilgisi silinemedi:', error);
      // Hata durumunda eski state'e geri dön
      setFormState(formState); // Revert to previous state
      if (window.showNotification) {
        window.showNotification('error', 'Silme sırasında bir sorun oluştu. Konsolu kontrol edin.');
      }
    }
  };

  // Google Maps script'ini yükle
  useEffect(() => {
    if (isLocationModalOpen && !mapLoaded) {
      // JavaScript API için key (modal içindeki harita)
      const jsApiKey = process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
      if (!jsApiKey) {
        console.error('Google Maps JavaScript API Key bulunamadı. Lütfen .env.local dosyasına REACT_APP_GOOGLE_MAPS_JS_API_KEY veya REACT_APP_GOOGLE_MAPS_API_KEY ekleyin.');
        setMapLoaded(false);
        return;
      }

      // Eğer Google Maps zaten yüklenmişse
      if (window.google && window.google.maps && window.google.maps.Map) {
        setMapLoaded(true);
        return;
      }

      // Mevcut script'i kontrol et
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        // Script yükleniyor, bekle
        let checkCount = 0;
        const maxChecks = 50; // 5 saniye max bekleme
        const checkGoogleMaps = () => {
          checkCount++;
          if (window.google && window.google.maps && window.google.maps.Map) {
            setMapLoaded(true);
          } else if (checkCount < maxChecks) {
            setTimeout(checkGoogleMaps, 100);
          } else {
            console.error('Google Maps yüklenemedi: Timeout');
            setMapLoaded(false);
          }
        };
        checkGoogleMaps();
        return;
      }

      // Callback için unique ID oluştur
      const callbackName = `initGoogleMaps_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Global callback fonksiyonu
      window[callbackName] = () => {
        try {
          if (window.google && window.google.maps && window.google.maps.Map) {
            setMapLoaded(true);
            console.log('Google Maps başarıyla yüklendi');
          } else {
            console.error('Google Maps yüklendi ama Map sınıfı bulunamadı.');
            setMapLoaded(false);
          }
        } catch (error) {
          console.error('Google Maps callback hatası:', error);
          console.error('Hata detayı: Maps JavaScript API aktif değil olabilir.');
          setMapLoaded(false);
        }
        // Callback'i temizle
        if (window[callbackName]) {
          delete window[callbackName];
        }
      };
      
      // Hata yakalama için global error handler
      const originalErrorHandler = window.gm_authFailure;
      window.gm_authFailure = () => {
        console.error('=== GOOGLE MAPS API KEY HATASI ===');
        console.error('ApiNotActivatedMapError: Maps JavaScript API aktif değil veya API key bu API\'ye erişemiyor.');
        console.error('API Key (ilk 10 karakter):', jsApiKey.substring(0, 10) + '...');
        console.error('');
        console.error('ÇÖZÜM ADIMLARI:');
        console.error('1. Google Cloud Console\'a gidin: https://console.cloud.google.com/');
        console.error('2. Doğru projeyi seçin');
        console.error('3. "APIs & Services" > "Library" bölümüne gidin');
        console.error('4. "Maps JavaScript API" arayın ve "ENABLE" butonuna tıklayın');
        console.error('5. API key sayfasına dönün ve "API restrictions" bölümünde "Maps JavaScript API" seçili olduğundan emin olun');
        console.error('6. "Save" butonuna tıklayın ve 5 dakika bekleyin');
        console.error('7. Development server\'ı yeniden başlatın (npm start)');
        console.error('===============================');
        setMapLoaded(false);
        if (originalErrorHandler) {
          originalErrorHandler();
        }
      };
      
      // Yeni script oluştur - loading=async parametresi ile
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${jsApiKey}&libraries=places&loading=async&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // onload callback zaten çalıştı, ek kontrol yapma
        console.log('Google Maps script yüklendi');
      };
      
      script.onerror = (error) => {
        console.error('Google Maps script yüklenemedi:', error);
        console.error('API Key:', jsApiKey.substring(0, 10) + '...');
        console.error('Hata: Maps JavaScript API aktif değil veya API key bu API\'ye erişemiyor.');
        console.error('Google Cloud Console\'da Maps JavaScript API\'nin aktif olduğundan ve API key\'in bu API\'ye erişim izni olduğundan emin olun.');
        setMapLoaded(false);
        // Callback'i temizle
        if (window[callbackName]) {
          delete window[callbackName];
        }
      };
      
      document.head.appendChild(script);
    }
    
    // Cleanup: Modal kapandığında mapLoaded'i sıfırlama (yeniden açıldığında hızlı yükleme için)
    return () => {
      // Cleanup gerekmiyor, mapLoaded state'i kalabilir
    };
  }, [isLocationModalOpen, mapLoaded]);

  // Pin butonuna tıklandığında
  const handlePinButtonClick = () => {
    // Pin zaten varsa, haritanın ortasına taşı
    if (pinVisible && pinPosition.lat !== null && pinPosition.lng !== null) {
      if (mapInstanceRef.current && mapInstanceRef.current.getCenter) {
        const center = mapInstanceRef.current.getCenter();
        if (center) {
          const lat = center.lat();
          const lng = center.lng();
          const currentZoom = mapInstanceRef.current.getZoom ? mapInstanceRef.current.getZoom() : mapZoom;
          setPinPosition({ lat, lng });
          setSelectedLocation({ lat, lng });
          setFormState(prev => ({
            ...prev,
            enlem: String(lat),
            boylam: String(lng),
            zoom: currentZoom,
          }));
        }
      }
    } else {
      // Pin yoksa, haritanın ortasına ekle
    let lat, lng;
      let currentZoom = mapZoom;
    
    if (mapInstanceRef.current && mapInstanceRef.current.getCenter) {
      const center = mapInstanceRef.current.getCenter();
      if (center) {
        lat = center.lat();
        lng = center.lng();
      } else {
        lat = mapCenter.lat;
        lng = mapCenter.lng;
      }
        if (mapInstanceRef.current.getZoom) {
          currentZoom = mapInstanceRef.current.getZoom();
        }
    } else {
      lat = mapCenter.lat;
      lng = mapCenter.lng;
    }
    
    setPinPosition({ lat, lng });
    setPinVisible(true);
    setSelectedLocation({ lat, lng });
    
    setFormState(prev => ({
      ...prev,
      enlem: String(lat),
      boylam: String(lng),
        zoom: currentZoom,
    }));
    }
  };

  // Pin sürüklendiğinde
  const handlePinDragEnd = useCallback((lat, lng) => {
    const currentZoom = mapInstanceRef.current && mapInstanceRef.current.getZoom ? mapInstanceRef.current.getZoom() : mapZoom;
    setPinPosition({ lat, lng });
    setSelectedLocation({ lat, lng });
    
    setFormState(prev => ({
      ...prev,
      enlem: String(lat),
      boylam: String(lng),
      zoom: currentZoom,
    }));
  }, [mapZoom]);

  // Konum kaydet
  const handleLocationSelect = () => {
    let lat, lng;
    let currentZoom = mapZoom;
    
    // Haritadan mevcut zoom değerini al
    if (mapInstanceRef.current && mapInstanceRef.current.getZoom) {
      const zoomFromMap = mapInstanceRef.current.getZoom();
      if (zoomFromMap !== null && zoomFromMap !== undefined) {
        currentZoom = zoomFromMap;
      }
    }
    
    if (markerInstanceRef.current && markerInstanceRef.current.getPosition) {
      const position = markerInstanceRef.current.getPosition();
      if (position) {
        lat = position.lat();
        lng = position.lng();
      } else {
        if (pinPosition.lat !== null && pinPosition.lng !== null) {
          lat = pinPosition.lat;
          lng = pinPosition.lng;
        } else {
          return;
        }
      }
    } else {
      if (pinPosition.lat !== null && pinPosition.lng !== null) {
        lat = pinPosition.lat;
        lng = pinPosition.lng;
      } else {
        return;
      }
    }
    
    console.log('Konum kaydediliyor:', { lat, lng, zoom: currentZoom });
    
    setFormState(prev => ({
      ...prev,
      enlem: String(lat),
      boylam: String(lng),
      zoom: currentZoom,
    }));
    setIsLocationModalOpen(false);
    setPinVisible(false);
    if (window.showNotification) {
      window.showNotification('success', 'Konum kaydedildi');
    }
  };

  // Konum seçimini iptal et
  const handleLocationCancel = () => {
    if (originalLocation.lat !== null && originalLocation.lng !== null) {
      setFormState(prev => ({
        ...prev,
        enlem: String(originalLocation.lat),
        boylam: String(originalLocation.lng),
        zoom: originalLocation.zoom !== null ? originalLocation.zoom : prev.zoom,
      }));
      setMapCenter({
        lat: originalLocation.lat,
        lng: originalLocation.lng
      });
      setMapZoom(originalLocation.zoom !== null ? originalLocation.zoom : 15);
      setPinPosition({ lat: originalLocation.lat, lng: originalLocation.lng });
      setSelectedLocation({ lat: originalLocation.lat, lng: originalLocation.lng });
    } else {
      setFormState(prev => ({
        ...prev,
        enlem: '',
        boylam: '',
      }));
      setPinPosition({ lat: null, lng: null });
      setSelectedLocation({ lat: null, lng: null });
    }
    setPinVisible(false);
    setIsLocationModalOpen(false);
  };

  // Harita merkezi değiştiğinde
  const handleMapCenterChanged = useCallback((lat, lng) => {
    setMapCenter({ lat, lng });
  }, []);

  // Harita zoom değiştiğinde
  const handleMapZoomChanged = useCallback((newZoom) => {
    setMapZoom(newZoom);
    setFormState(prev => ({
      ...prev,
      zoom: newZoom,
    }));
  }, []);

  const closeLocationModal = () => {
    setIsLocationModalOpen(false);
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="content-wrapper">
          <div className="loading-state">Yükleniyor...</div>
        </div>
      </main>
    );
  }

  const hasLocation = selectedLocation.lat !== null && selectedLocation.lng !== null;

  return (
    <>
      <NotificationStack />
    <main className="main-content">
        <div className="content-header">
          <div>
            <h1>{currentMode === 'create' ? 'Teknik Servis Ekle' : 'Teknik Servisi Düzenle'}</h1>
          </div>
        </div>

        <div className="tabs">
          {TECH_SERVICE_TABS.map((tab) => (
          <button
              key={tab.key}
            type="button"
              className={`tab-btn ${activeSectionTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveSectionTab(tab.key)}
          >
              {tab.label}
          </button>
          ))}
        </div>

        {formError && <div className="error-state small">{safeText(formError)}</div>}

        <div className="tab-panel">
          {activeSectionTab === 'service' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Teknik Servis Bilgileri</FieldLabel>
                <div className="form-grid">
              <label className="form-field">
                <span>Teknik Servis Adı *</span>
                <input
                  type="text"
                  name="ad"
                  value={formState.ad}
                  onChange={handleFormChange}
                  maxLength={100}
                  minLength={2}
                      placeholder="Teknik Servis Adı (Max. 100 karakter)"
                  required
                />
              </label>
              <label className="form-field">
                    <span>Adres *</span>
                <input
                  type="text"
                  name="adres"
                  value={formState.adres}
                  onChange={handleFormChange}
                  maxLength={500}
                  minLength={10}
                      placeholder="Adres Bilgisi (Max. 500 karakter)"
                  required
                />
              </label>
              <label className="form-field">
                <span>İl *</span>
                <select
                  name="cityId"
                  value={formState.cityId}
                  onChange={(e) => {
                    console.log('[TechServiceFormPage] İl onChange, value:', e.target.value);
                    setFormState((prev) => ({
                      ...prev,
                      cityId: e.target.value,
                      districtId: ''
                    }));
                    setIlceler([]);
                  }}
                  required
                  disabled={illerLoading}
                >
                  <option value="">İl Seçiniz</option>
                  {illerLoading ? (
                    <option disabled>İller yükleniyor...</option>
                  ) : iller.length === 0 ? (
                    <option disabled>İl bulunamadı</option>
                  ) : (
                    iller.map((il) => (
                      <option key={il.id || il._id} value={String(il.id || il._id)}>
                        {il.name || il.ad}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <label className="form-field">
                <span>İlçe *</span>
                <select
                  name="districtId"
                  value={formState.districtId || ''}
                  onChange={handleFormChange}
                  required
                  disabled={!formState.cityId || ilcelerLoading}
                >
                  <option value="">İlçe Seçiniz</option>
                  {ilcelerLoading ? (
                    <option disabled>İlçeler yükleniyor...</option>
                  ) : ilceler.length === 0 ? (
                    <option disabled>İlçe bulunamadı (Toplam: {ilceler.length})</option>
                  ) : (
                    ilceler.map((ilce, index) => {
                      // API formatı: { id: number, adi: string, cityId: number }
                      const ilceId = ilce?.id || ilce?._id;
                      const ilceName = ilce?.adi || ilce?.ad || ilce?.name || 'İsimsiz İlçe';
                      
                      if (!ilceId) {
                        console.warn('[Select] Geçersiz ilçe verisi:', ilce, 'Index:', index);
                        return null;
                      }
                      
                      return (
                        <option key={ilceId} value={String(ilceId)}>
                          {ilceName}
                        </option>
                      );
                    }).filter(Boolean)
                  )}
                </select>
              </label>
                </div>
              </div>
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Sistem Bilgileri</FieldLabel>
                <div className="tech-service-readonly-meta">
                  <div className="tech-service-readonly-meta__row">
                    <span className="tech-service-readonly-meta__label">Teknik servis kodu</span>
                    <span className="tech-service-readonly-meta__value">
                      {formState.kod && String(formState.kod).trim() !== ''
                        ? String(formState.kod)
                        : currentMode === 'create'
                          ? 'Kayıt oluşturulduktan sonra atanır'
                          : '—'}
                    </span>
                  </div>
                  <div className="tech-service-readonly-meta__row">
                    <span className="tech-service-readonly-meta__label">Panel kullanıcısı</span>
                    <span className="tech-service-readonly-meta__value tech-service-readonly-meta__value--actions">
                      {currentMode === 'create'
                        ? 'Kayıt sonrası görünür'
                        : teknikServisKullanicilari.length === 0
                          ? (
                            <button
                              type="button"
                              className="primary-btn tech-service-panel-user-create-btn"
                              onClick={() => {
                                setPanelUserModalState({ kullaniciAdi: '', sifre: '' });
                                setPanelUserModalError('');
                                setIsPanelUserModalOpen(true);
                              }}
                            >
                              <i className="fas fa-user-plus" aria-hidden />
                              Kullanıcı oluştur
                            </button>
                            )
                          : teknikServisKullanicilari.map((ku, i) => {
                              const uid = ku.id || ku._id;
                              const label = decodeHtml(ku.kullaniciAdi || '—');
                              return (
                                <span key={uid ? String(uid) : `ku-${i}`}>
                                  {i > 0 ? ', ' : null}
                                  {uid ? (
                                    <Link
                                      to={`/users/${uid}/edit`}
                                      className="tech-service-readonly-meta__link"
                                    >
                                      {label}
                                    </Link>
                                  ) : (
                                    label
                                  )}
                                </span>
                              );
                            })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="section-block">
                <FieldLabel className="section-title" as="div">İletişim Bilgileri</FieldLabel>
                <div className="form-grid">
                  <label className="form-field">
                    <span>Telefon *</span>
                    <input
                      type="tel"
                      name="telefon"
                      value={formState.telefon}
                      onChange={handleFormChange}
                      placeholder="10 haneli (Başında 0 olmadan)"
                      maxLength={10}
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>E-posta *</span>
                    <input
                      type="email"
                      name="mail"
                      value={formState.mail}
                      onChange={handleFormChange}
                      placeholder="ornek@email.com"
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>Yetkili Kişi</span>
                    <input
                      type="text"
                      name="yetkiliKisi"
                      value={formState.yetkiliKisi}
                      onChange={handleFormChange}
                      maxLength={100}
                      placeholder="Yetkili kişi adı"
                    />
                  </label>
                  <label className="form-field">
                    <span>Yetkili Telefon</span>
                    <input
                      type="tel"
                      name="yetkiliTelefon"
                      value={formState.yetkiliTelefon}
                      onChange={handleFormChange}
                      placeholder="10 haneli (Başında 0 olmadan)"
                      maxLength={10}
                    />
                  </label>
                  <label className="form-field">
                    <span>Faks</span>
                    <input
                      type="text"
                      name="faks"
                      value={formState.faks}
                      onChange={handleFormChange}
                      placeholder="Faks numarası"
                      maxLength={40}
                    />
                  </label>
                  <label className="form-field">
                    <span>Katılma tarihi</span>
                    <input
                      type="date"
                      name="katilmaTarihi"
                      value={formState.katilmaTarihi}
                      onChange={handleFormChange}
                    />
                  </label>
                </div>
              </div>
              <div className="section-actions">
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleSectionSave('service')}
                  disabled={formSubmitting || !hasSectionChanges('service')}
                >
                  {formSubmitting ? 'Kaydediliyor...' : 'Servis Bilgilerini Kaydet'}
                </button>
              </div>
            </div>
          )}

          {activeSectionTab === 'location' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Konum Bilgileri</FieldLabel>
                <div className="location-section-grid">
                  <div className="location-preview-container">
                    {hasLocation ? (
                      <div className="location-map-preview">
                        {(process.env.REACT_APP_GOOGLE_MAPS_EMBED_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY) ? (
                          <iframe
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_EMBED_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${selectedLocation.lat},${selectedLocation.lng}&zoom=17&maptype=roadmap`}
                            width="100%"
                            height="100%"
                            style={{
                              border: 0,
                              minHeight: '300px',
                              borderRadius: '8px',
                            }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Konum Önizlemesi"
                            onError={(e) => {
                              // Hata durumunda OpenStreetMap'e geç
                              e.target.style.display = 'none';
                              const osmMap = e.target.nextElementSibling;
                              if (osmMap) {
                                osmMap.style.display = 'block';
                              }
                            }}
                          />
                        ) : null}
                        <iframe
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedLocation.lng - 0.01},${selectedLocation.lat - 0.01},${selectedLocation.lng + 0.01},${selectedLocation.lat + 0.01}&layer=mapnik&marker=${selectedLocation.lat},${selectedLocation.lng}`}
                          width="100%"
                          height="100%"
                          style={{
                            border: 0,
                            minHeight: '300px',
                            borderRadius: '8px',
                            display: (process.env.REACT_APP_GOOGLE_MAPS_EMBED_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY) ? 'none' : 'block',
                          }}
                          allowFullScreen
                          loading="lazy"
                          title="Konum Önizlemesi"
                        />
                      </div>
                    ) : (
                      <div className="location-empty-preview">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>Konum ayarlanmadı</span>
                      </div>
                    )}
                  </div>
                  <div className="location-coordinates-container">
                    <label className="form-field">
                      <span>Enlem</span>
                  <input
                    type="number"
                    name="enlem"
                    value={formState.enlem}
                    onChange={handleFormChange}
                    placeholder="Enlem (örn: 41.0082)"
                    step="any"
                    min="-90"
                    max="90"
                    readOnly
                  />
                    </label>
                    <label className="form-field">
                      <span>Boylam</span>
                  <input
                    type="number"
                    name="boylam"
                    value={formState.boylam}
                    onChange={handleFormChange}
                    placeholder="Boylam (örn: 28.9784)"
                    step="any"
                    min="-180"
                    max="180"
                    readOnly
                  />
                    </label>
                  </div>
                </div>
              </div>
              <div className="section-actions">
                  <button
                    type="button"
                    className="primary-btn"
                  onClick={openLocationSelector}
                  disabled={formSubmitting}
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    Konum Seç
                  </button>
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleSectionSave('location')}
                  disabled={formSubmitting || !hasSectionChanges('location')}
                >
                  {formSubmitting ? 'Kaydediliyor...' : 'Konum Bilgilerini Kaydet'}
                </button>
                </div>
            </div>
          )}

          {activeSectionTab === 'company' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Şirket Bilgileri</FieldLabel>
                <div className="form-field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)' }}>Şirketler</span>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => openCompanyModal('create')}
                    >
                      <i className="fas fa-plus"></i>
                      Şirket Bilgisi Ekle
                    </button>
                  </div>
                  {formState.sirketBilgileri && formState.sirketBilgileri.length > 0 ? (
                    <div className="table-container">
                      <table className="admins-table company-table">
                        <thead>
                          <tr>
                            <th>Şirket Adı</th>
                            <th>Tip</th>
                            <th>Vergi Dairesi</th>
                            <th>Vergi No</th>
                            <th>Adres</th>
                            <th className="islemler-sutunu">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formState.sirketBilgileri.map((sirket, index) => (
                            <tr key={index}>
                              <td>{sirket.sirketAdi || '—'}</td>
                              <td>{sirket.sirketTipi || '—'}</td>
                              <td>{sirket.vergiDairesi || '—'}</td>
                              <td>{sirket.vergiNumarasi || '—'}</td>
                              <td>{sirket.sirketAdresi || '—'}</td>
                              <td className="table-actions islemler-sutunu">
                                <button
                                  type="button"
                                  className="icon-btn"
                                  onClick={() => openCompanyModal('edit', index)}
                                  title="Düzenle"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  type="button"
                                  className="icon-btn danger"
                                  onClick={() => handleCompanyDelete(index)}
                                  title="Sil"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                      <i className="fas fa-building" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                      <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Henüz şirket bilgisi eklenmemiş</div>
                    </div>
                  )}
            </div>
          </div>
            </div>
          )}

          {activeSectionTab === 'bank' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Banka Bilgileri</FieldLabel>
                <div className="form-field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)' }}>Banka Hesapları</span>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => openBankModal('create')}
                  >
                    <i className="fas fa-plus"></i>
                    Banka Bilgisi Ekle
                  </button>
                </div>
                {formState.bankaBilgileri && formState.bankaBilgileri.length > 0 ? (
                    <div className="table-container">
                      <table className="admins-table company-table">
                        <thead>
                          <tr>
                            <th>Banka</th>
                            <th>Hesap Adı</th>
                            <th>IBAN</th>
                            <th className="islemler-sutunu">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                    {formState.bankaBilgileri.map((banka, index) => {
                            // Banka ID'sini al - string olmalı (yukarıda formatlanmış)
                            const bankaIdValue = banka.bankaId || '';
                            const bankaIdStr = String(bankaIdValue);
                            
                            // Bankalar listesinde eşleştir
                            const bankaInfo = bankalar.find((b) => {
                              const bId = String(b.id || b._id || '');
                              return bId === bankaIdStr && bId !== '';
                            });
                            
                            // Debug: Eğer banka bulunamazsa log'la
                            if (!bankaInfo && bankaIdValue) {
                              console.log('=== Banka Bulunamadı ===');
                              console.log('Aradığımız Banka ID:', bankaIdValue, '(tip:', typeof bankaIdValue, ')');
                              console.log('Aradığımız Banka ID (String):', bankaIdStr);
                              console.log('Mevcut Bankalar:', bankalar.map(b => ({
                                id: b.id || b._id,
                                idType: typeof (b.id || b._id),
                                idString: String(b.id || b._id),
                                name: b.name || b.ad
                              })));
                              console.log('Banka Bilgisi:', banka);
                              console.log('========================');
                            }
                            
                      return (
                              <tr key={index}>
                                <td>{bankaInfo?.name || bankaInfo?.ad || 'Bilinmeyen Banka'}</td>
                                <td>{banka.hesapAdi || '—'}</td>
                                <td>{banka.iban || '—'}</td>
                                <td className="table-actions islemler-sutunu">
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => openBankModal('edit', index)}
                              title="Düzenle"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                                    className="icon-btn danger"
                              onClick={() => handleBankDelete(index)}
                              title="Sil"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                                </td>
                              </tr>
                      );
                    })}
                        </tbody>
                      </table>
                  </div>
                ) : (
                    <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                      <i className="fas fa-university" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                      <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Henüz banka bilgisi eklenmemiş</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {activeSectionTab === 'authorized' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Yetkili Alanlar</FieldLabel>
                <div className="form-field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)' }}>Yetkili Servis Alanları</span>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={openAuthorizedAreaModal}
                    >
                      <i className="fas fa-plus"></i>
                      Yetkili Servis Alanı Ekle
                    </button>
                  </div>
                  {serviceAreasLoading ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: 'var(--primary-blue)', marginBottom: '12px' }}></i>
                      <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Yükleniyor...</div>
                    </div>
                  ) : serviceAreas && Array.isArray(serviceAreas) && serviceAreas.length > 0 ? (
                    <div className="table-container">
                      <table className="admins-table company-table">
                        <thead>
                          <tr>
                            <th>Yetkili Olduğu İl</th>
                            <th>Yetkili Olduğu İlçe</th>
                            <th>Yetkili Ürün Kategorileri</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Her yetkili alanı (ilçe) için ayrı satır - ilçe ve kategori aynı satırda hizalı
                            const rows = [];
                            serviceAreas.forEach((area, areaIndex) => {
                              const cityName = area.il?.name || area.il?.ad || 'Bilinmeyen İl';
                              const districtName = area.ilce?.adi || area.ilce?.ad || area.ilce?.name || 'Bilinmeyen İlçe';
                              rows.push({
                                key: area.id || area._id || areaIndex,
                                cityName,
                                districtName,
                                area,
                                areaId: area.id || area._id,
                              });
                            });
                            return rows.map((row) => (
                              <tr key={row.key}>
                                <td style={{ verticalAlign: 'middle' }}>{row.cityName}</td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  <div style={{ fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                    <span>{row.districtName}</span>
                                    <button
                                      type="button"
                                      className="icon-btn danger"
                                      onClick={() => handleAuthorizedAreaDelete(row.areaId, row.districtName)}
                                      title="Sil"
                                      style={{ flexShrink: 0, padding: '4px 8px' }}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                                <td style={{ verticalAlign: 'middle' }}>
                                  <ServiceAreaCategoryDetail
                                    area={row.area}
                                    allCategories={authorizedAreaProductCategories}
                                    confirm={confirm}
                                    onAreaUpdated={handleServiceAreaCategoriesUpdated}
                                    onAreaDeleted={(areaId) =>
                                      handleAuthorizedAreaDelete(areaId, row.districtName)
                                    }
                                  />
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                      <i className="fas fa-map-marker-alt" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                      <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Henüz yetkili alan eklenmemiş</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSectionTab === 'technicians' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    marginBottom: '8px',
                  }}
                >
                  <div className="section-title" style={{ marginBottom: 0 }}>
                    Teknisyenler
                  </div>
                  {currentMode === 'edit' && id ? (
                    <button
                      type="button"
                      className="primary-btn tech-service-panel-user-create-btn"
                      onClick={() => {
                        setTechnicianModalState({
      kullaniciAdi: '',
      sifre: '',
      orderCreationPermission: ORDER_CREATION_PERMISSION_DEFAULT,
    });
                        setTechnicianModalError('');
                        setIsTechnicianModalOpen(true);
                      }}
                    >
                      <i className="fas fa-user-plus" aria-hidden />
                      Teknisyen Ekle
                    </button>
                  ) : null}
                </div>
                <p style={{ fontSize: '14px', color: 'var(--text-gray)', marginTop: 0, marginBottom: '16px', lineHeight: 1.5 }}>
                  Bu teknik servise bağlı teknisyen hesapları. Düzenleme için Kullanıcı Yönetimi&apos;ni kullanın.
                </p>
                {currentMode === 'create' || !id ? (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <i className="fas fa-user-cog" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                    <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Önce teknik servisi kaydedin; ardından teknisyenleri burada görebilirsiniz.</div>
                  </div>
                ) : serviceTabTechniciansError ? (
                  <div className="error-state small">{safeText(serviceTabTechniciansError)}</div>
                ) : serviceTabTechniciansLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: 'var(--primary-blue)', marginBottom: '12px' }}></i>
                    <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Yükleniyor...</div>
                  </div>
                ) : serviceTabTechnicians.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <i className="fas fa-users" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                    <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Bu teknik servise kayıtlı teknisyen yok.</div>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="admins-table company-table">
                      <thead>
                        <tr>
                          <th>Kullanıcı adı</th>
                          <th>Ad</th>
                          <th>Soyad</th>
                          <th>Telefon</th>
                          <th>Sipariş yetkisi</th>
                          <th>Eklenme</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serviceTabTechnicians.map((t) => (
                          <tr key={t._id || t.id}>
                            <td>{decodeHtml(t.kullaniciAdi || '—')}</td>
                            <td>{decodeHtml(t.ad || '—')}</td>
                            <td>{decodeHtml(t.soyad || '—')}</td>
                            <td>{t.telefon || '—'}</td>
                            <td>
                              <OrderCreationPermissionBadge value={t.orderCreationPermission} />
                            </td>
                            <td>{formatOlusturmaTarihiTr(t.olusturulma)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSectionTab === 'cariHareketler' && (
            <div className="tab-panel-card">
              {currentMode === 'create' || !id ? (
                <div className="section-block">
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <i className="fas fa-file-invoice-dollar" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                    <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
                      Önce teknik servisi kaydedin; ardından cari hareketleri burada görebilirsiniz.
                    </div>
                  </div>
                </div>
              ) : (
                <CariHareketlerPanel
                  teknikServisId={id}
                  teknikServisAdi={formState.ad}
                  embedded
                  active={activeSectionTab === 'cariHareketler'}
                />
              )}
            </div>
          )}

          {activeSectionTab === 'stok' && (
            <div className="tab-panel-card">
              {currentMode === 'create' || !id ? (
                <div className="section-block">
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <i className="fas fa-warehouse" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
                    <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
                      Önce teknik servisi kaydedin; ardından stok bilgilerini burada görebilirsiniz.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="teknik-servis-stok-tab-layout">
                  <KonsinyeCariLimitCard
                    techServiceId={id}
                    canEditLimit
                  />
                  <TeknikServisStokDetayPanel
                    teknikServisId={id}
                    teknikServisAdi={formState.ad}
                    teknikServisKod={formState.kod}
                    embedded
                    showServiceHeader
                    active={activeSectionTab === 'stok'}
                  />
                </div>
              )}
            </div>
          )}

          {activeSectionTab === 'other' && (
            <div className="tab-panel-card">
              <div className="section-block">
                <FieldLabel className="section-title" as="div">Diğer</FieldLabel>
            <div className="form-grid">
                  <label className="form-field">
                <span>Açıklama</span>
                <textarea
                  name="aciklama"
                  value={formState.aciklama}
                  onChange={handleFormChange}
                  maxLength={1000}
                  rows={3}
                  placeholder="Açıklama (opsiyonel)"
                />
                    <small>Maksimum 1000 karakter</small>
              </label>
              <label className="form-field checkbox-field">
                <input
                  type="checkbox"
                  name="isTest"
                  checked={formState.isTest === true}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, isTest: e.target.checked }))
                  }
                />
                <span>Test teknik servisi</span>
              </label>
            </div>
          </div>
              <div className="section-actions">
                <button
                  type="button"
                  className="primary-btn"
                  onClick={handleSectionSave('other')}
                  disabled={formSubmitting || !hasSectionChanges('other')}
                >
                  {formSubmitting ? 'Kaydediliyor...' : 'Diğer Bilgileri Kaydet'}
            </button>
          </div>
            </div>
          )}
        </div>

        {/* Panel kullanıcısı — Kullanıcı yönetimi ile aynı kayıt kuralı (rol + TS sabit) */}
        {isPanelUserModalOpen && (
          <div
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="panel-user-modal-title"
            onClick={closePanelUserModal}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="panel-user-modal-title">Panel kullanıcısı oluştur</h2>
                <button type="button" className="close-btn" onClick={closePanelUserModal} aria-label="Kapat">
                  <i className="fas fa-times" />
                </button>
              </div>
              <form onSubmit={handlePanelUserModalSubmit} className="modal-body">
                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-gray)',
                    margin: '0 0 16px 0',
                    lineHeight: 1.5,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'rgba(0, 0, 0, 0.02)',
                  }}
                >
                  Her teknik serviste yalnızca <strong>bir</strong> panel (Teknik Servis) hesabı tanımlanabilir.
                  Kullanıcı adı ve şifre kuralları, kullanıcı yönetimindeki &quot;Kullanıcı Ekle&quot; ile aynıdır.
                </p>
                <div className="form-grid">
                  <div className="form-field">
                    <span>Rol</span>
                    <div
                      style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-subtle, rgba(0,0,0,0.03))',
                        fontSize: '14px',
                        color: 'var(--text-dark)',
                      }}
                    >
                      Teknik Servis
                    </div>
                  </div>
                  <div className="form-field full-width">
                    <span>Teknik servis</span>
                    <div
                      className="selected-tech-service"
                      style={{ marginTop: 0, cursor: 'default' }}
                    >
                      <div>
                        <span className="tech-service-selection-title">
                          {decodeHtml(formState.ad || '—')}
                        </span>
                        <div className="tech-service-selection-subtitle">
                          {formState.kod != null && String(formState.kod).trim() !== ''
                            ? `Kod: ${formState.kod} · `
                            : ''}
                          {panelUserModalTechLocation}
                        </div>
                      </div>
                    </div>
                  </div>
                  <label className="form-field">
                    <span>Kullanıcı adı *</span>
                    <input
                      type="text"
                      name="panelKullaniciAdi"
                      value={panelUserModalState.kullaniciAdi}
                      onChange={(e) =>
                        setPanelUserModalState((prev) => ({
                          ...prev,
                          kullaniciAdi: e.target.value,
                        }))
                      }
                      maxLength={50}
                      minLength={3}
                      pattern="[a-zA-Z0-9_]+"
                      placeholder="Kullanıcı adı"
                      autoComplete="off"
                      required
                    />
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      Sadece harf, rakam ve alt çizgi (3–50 karakter)
                    </small>
                  </label>
                  <label className="form-field">
                    <span>Şifre *</span>
                    <input
                      type="password"
                      name="panelSifre"
                      value={panelUserModalState.sifre}
                      onChange={(e) =>
                        setPanelUserModalState((prev) => ({
                          ...prev,
                          sifre: e.target.value,
                        }))
                      }
                      minLength={6}
                      maxLength={100}
                      placeholder="En az 6 karakter"
                      autoComplete="new-password"
                      required
                    />
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {panelUserModalState.sifre.length} / 100 — küçük harf, büyük harf ve rakam
                    </small>
                  </label>
                </div>
                {panelUserModalError && (
                  <div className="error-state small" style={{ marginTop: '12px' }}>
                    {safeText(panelUserModalError)}
                  </div>
                )}
                <div className="modal-footer">
                  <button type="button" className="secondary-btn" onClick={closePanelUserModal}>
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={panelUserModalSubmitting || !isPanelUserModalValid}
                  >
                    {panelUserModalSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Teknisyen hesabı — createUser tech-person + mevcut teknik servis (Kullanıcı Ekle ile aynı kurallar) */}
        {isTechnicianModalOpen && (
          <div
            className="modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="technician-user-modal-title"
            onClick={closeTechnicianModal}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 id="technician-user-modal-title">Teknisyen hesabı oluştur</h2>
                <button type="button" className="close-btn" onClick={closeTechnicianModal} aria-label="Kapat">
                  <i className="fas fa-times" />
                </button>
              </div>
              <form onSubmit={handleTechnicianModalSubmit} className="modal-body">
                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-gray)',
                    margin: '0 0 16px 0',
                    lineHeight: 1.5,
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'rgba(0, 0, 0, 0.02)',
                  }}
                >
                  Bu teknik servise birden fazla teknisyen hesabı eklenebilir. Kullanıcı adı ve şifre kuralları,
                  kullanıcı yönetimindeki &quot;Kullanıcı Ekle&quot; ile aynıdır.
                </p>
                <div className="form-grid">
                  <div className="form-field">
                    <span>Rol</span>
                    <div
                      style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-subtle, rgba(0,0,0,0.03))',
                        fontSize: '14px',
                        color: 'var(--text-dark)',
                      }}
                    >
                      Teknisyen
                    </div>
                  </div>
                  <div className="form-field full-width">
                    <span>Teknik servis</span>
                    <div
                      className="selected-tech-service"
                      style={{ marginTop: 0, cursor: 'default' }}
                    >
                      <div>
                        <span className="tech-service-selection-title">
                          {decodeHtml(formState.ad || '—')}
                        </span>
                        <div className="tech-service-selection-subtitle">
                          {formState.kod != null && String(formState.kod).trim() !== ''
                            ? `Kod: ${formState.kod} · `
                            : ''}
                          {panelUserModalTechLocation}
                        </div>
                      </div>
                    </div>
                  </div>
                  <label className="form-field">
                    <span>Kullanıcı adı *</span>
                    <input
                      type="text"
                      name="technicianKullaniciAdi"
                      value={technicianModalState.kullaniciAdi}
                      onChange={(e) =>
                        setTechnicianModalState((prev) => ({
                          ...prev,
                          kullaniciAdi: e.target.value,
                        }))
                      }
                      maxLength={50}
                      minLength={3}
                      pattern="[a-zA-Z0-9_]+"
                      placeholder="Kullanıcı adı"
                      autoComplete="off"
                      required
                    />
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      Sadece harf, rakam ve alt çizgi (3–50 karakter)
                    </small>
                  </label>
                  <label className="form-field">
                    <span>Şifre *</span>
                    <input
                      type="password"
                      name="technicianSifre"
                      value={technicianModalState.sifre}
                      onChange={(e) =>
                        setTechnicianModalState((prev) => ({
                          ...prev,
                          sifre: e.target.value,
                        }))
                      }
                      minLength={6}
                      maxLength={100}
                      placeholder="En az 6 karakter"
                      autoComplete="new-password"
                      required
                    />
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {technicianModalState.sifre.length} / 100 — küçük harf, büyük harf ve rakam
                    </small>
                  </label>
                  <label className="form-field full-width">
                    <span>Sipariş Oluşturma Yetkisi</span>
                    <select
                      value={technicianModalState.orderCreationPermission}
                      onChange={(e) =>
                        setTechnicianModalState((prev) => ({
                          ...prev,
                          orderCreationPermission: e.target.value,
                        }))
                      }
                    >
                      {ORDER_CREATION_PERMISSION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {technicianModalError && (
                  <div className="error-state small" style={{ marginTop: '12px' }}>
                    {safeText(technicianModalError)}
                  </div>
                )}
                <div className="modal-footer">
                  <button type="button" className="secondary-btn" onClick={closeTechnicianModal}>
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={technicianModalSubmitting || !isTechnicianModalValid}
                  >
                    {technicianModalSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Google Maps Konum Seçici Modal */}
        {isLocationModalOpen && (
          <div 
            className="modal-overlay" 
            role="dialog" 
            aria-modal="true" 
            style={{ zIndex: 10001 }}
          >
            <div 
              className="modal location-modal" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Konum Seç</h2>
                <button type="button" className="close-btn" onClick={closeLocationModal} aria-label="Kapat">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body" style={{ flex: 1, padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {mapLoaded ? (
                  <>
                    <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <LocationPickerMap
                        center={mapCenter}
                        zoom={mapZoom}
                        onZoomChange={handleMapZoomChanged}
                        onCenterChange={handleMapCenterChanged}
                        pinVisible={pinVisible}
                        pinPosition={pinPosition}
                        onPinDragEnd={handlePinDragEnd}
                        mapInstanceRef={mapInstanceRef}
                        markerRef={markerInstanceRef}
                      />
                      <button
                        type="button"
                        onClick={handlePinButtonClick}
                        style={{
                          position: 'absolute',
                          right: '20px',
                          top: '20px',
                          backgroundColor: 'white',
                          border: '2px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: 'var(--text-dark)',
                          zIndex: 1000,
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                          e.currentTarget.style.borderColor = 'var(--primary-blue)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = 'var(--border-color)';
                        }}
                      >
                        <i className="fas fa-map-pin" style={{ color: 'var(--primary-blue)' }}></i>
                        {pinVisible ? 'Pin Taşı' : 'Pin Ekle'}
                      </button>
                    </div>
                    <div style={{ padding: '16px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        {pinPosition.lat !== null && pinPosition.lng !== null ? (
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                              <strong style={{ color: 'var(--text-dark)', marginRight: '8px' }}>Enlem:</strong>
                              {pinPosition.lat.toFixed(6)}
                            </div>
                            <div style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                              <strong style={{ color: 'var(--text-dark)', marginRight: '8px' }}>Boylam:</strong>
                              {pinPosition.lng.toFixed(6)}
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
                            Haritayı istediğiniz yere götürün ve "Pin Ekle" butonuna tıklayın
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={handleLocationCancel}
                        >
                          <i className="fas fa-times"></i>
                          İptal
                        </button>
                        <button
                          type="button"
                          className="primary-btn"
                          onClick={handleLocationSelect}
                          disabled={pinPosition.lat === null || pinPosition.lng === null}
                        >
                          <i className="fas fa-check"></i>
                          Konumu Kaydet
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                    <div style={{ textAlign: 'center' }}>
                      {(process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY) ? (
                        <>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: 'var(--primary-blue)', marginBottom: '16px' }}></i>
                      <div>Google Maps yükleniyor...</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '8px' }}>
                            Hata alıyorsanız console'u kontrol edin
                          </div>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-exclamation-triangle" style={{ fontSize: '32px', color: 'var(--warning-orange)', marginBottom: '16px' }}></i>
                          <div style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>Google Maps API Key bulunamadı</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-gray)' }}>Lütfen .env.local dosyasına REACT_APP_GOOGLE_MAPS_API_KEY ekleyin</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Şirket Bilgileri Modal */}
        {isCompanyModalOpen && (
          <div 
            className="modal-overlay" 
            role="dialog" 
            aria-modal="true" 
            style={{ zIndex: 10002 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="modal" 
              style={{ maxWidth: '700px', width: '90vw', zIndex: 10003 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{companyModalMode === 'create' ? 'Şirket Bilgisi Ekle' : 'Şirket Bilgisini Düzenle'}</h2>
                <button 
                  type="button" 
                  className="close-btn" 
                  onClick={closeCompanyModal} 
                  aria-label="Kapat"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <label className="form-field">
                    <span>Şirket Tipi *</span>
                    <select
                      name="sirketTipi"
                      value={companyFormState.sirketTipi}
                      onChange={handleCompanyFormChange}
                      required
                    >
                      <option value="">Şirket Tipi Seçiniz</option>
                      <option value="Şahıs">Şahıs</option>
                      <option value="Kurumsal">Kurumsal</option>
                    </select>
                  </label>
                  <label className="form-field">
                    <span>Şirket Adı *</span>
                    <input
                      type="text"
                      name="sirketAdi"
                      value={companyFormState.sirketAdi}
                      onChange={handleCompanyFormChange}
                      maxLength={200}
                      placeholder="Şirket adı"
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>Vergi Dairesi *</span>
                    <input
                      type="text"
                      name="vergiDairesi"
                      value={companyFormState.vergiDairesi}
                      onChange={handleCompanyFormChange}
                      maxLength={100}
                      placeholder="Vergi dairesi adı"
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>Vergi Numarası *</span>
                    <input
                      type="text"
                      name="vergiNumarasi"
                      value={companyFormState.vergiNumarasi}
                      onChange={handleCompanyFormChange}
                      maxLength={11}
                      placeholder="Vergi Numarası"
                      required
                      pattern="\d{10,11}"
                    />
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      10 veya 11 haneli, sadece rakam
                    </small>
                  </label>
                  <label className="form-field" style={{ gridColumn: 'span 2' }}>
                    <span>Şirket Adresi *</span>
                    <input
                      type="text"
                      name="sirketAdresi"
                      value={companyFormState.sirketAdresi}
                      onChange={handleCompanyFormChange}
                      maxLength={500}
                      placeholder="Şirket adresi"
                      required
                    />
                  </label>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="secondary-btn" 
                    onClick={closeCompanyModal}
                  >
                    İptal
                  </button>
                  <button 
                    type="button" 
                    className="primary-btn" 
                    onClick={handleCompanySave}
                  >
                    <i className="fas fa-save"></i>
                    Şirket Bilgileri Kaydet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Banka Bilgileri Modal */}
        {isBankModalOpen && (
          <div 
            className="modal-overlay" 
            role="dialog" 
            aria-modal="true" 
            style={{ zIndex: 10002 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="modal" 
              style={{ maxWidth: '700px', width: '90vw', zIndex: 10003 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{bankModalMode === 'create' ? 'Banka Bilgisi Ekle' : 'Banka Bilgisini Düzenle'}</h2>
                <button 
                  type="button" 
                  className="close-btn" 
                  onClick={closeBankModal} 
                  aria-label="Kapat"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <label className="form-field">
                    <span>Banka *</span>
                    <select
                      name="bankaId"
                      value={bankFormState.bankaId}
                      onChange={handleBankFormChange}
                      required
                      disabled={bankalarLoading}
                    >
                      <option value="">Banka Seçiniz</option>
                      {bankalarLoading ? (
                        <option disabled>Bankalar yükleniyor...</option>
                      ) : bankalar.length === 0 ? (
                        <option disabled>Banka bulunamadı</option>
                      ) : (
                        bankalar.map((banka) => (
                          <option key={banka.id || banka._id} value={banka.id || banka._id}>
                            {banka.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <label className="form-field">
                    <span>Hesap Adı *</span>
                    <input
                      type="text"
                      name="hesapAdi"
                      value={bankFormState.hesapAdi}
                      onChange={handleBankFormChange}
                      maxLength={100}
                      placeholder="Hesap adı"
                      required
                    />
                  </label>
                  <label className="form-field" style={{ gridColumn: 'span 2' }}>
                    <span>IBAN *</span>
                    <input
                      type="text"
                      name="iban"
                      value={bankFormState.iban}
                      onChange={handleBankFormChange}
                      maxLength={26}
                      placeholder="TR330006100519786457841326"
                      style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                      required
                    />
                    <small style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      TR ile başlayan 26 karakter (boşluklar otomatik temizlenir)
                    </small>
                  </label>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="secondary-btn" 
                    onClick={closeBankModal}
                  >
                    İptal
                  </button>
                  <button 
                    type="button" 
                    className="primary-btn" 
                    onClick={handleBankSave}
                  >
                    <i className="fas fa-save"></i>
                    Banka Bilgileri Kaydet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Yetkili Alan Modal */}
        {isAuthorizedAreaModalOpen && (
          <div 
            className="modal-overlay" 
            role="dialog" 
            aria-modal="true" 
            style={{ zIndex: 10002 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeAuthorizedAreaModal();
              }
            }}
          >
            <div 
              className="modal" 
              style={{ maxWidth: '700px', width: '90vw', zIndex: 10003 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Yetkili Servis Alanı Ekle</h2>
                <button 
                  type="button" 
                  className="close-btn" 
                  onClick={closeAuthorizedAreaModal}
                  aria-label="Kapat"
                >
                  <i className="fas fa-times"></i>
                </button>
      </div>
              <div className="modal-body">
                <div className="form-grid">
                  <label className="form-field">
                    <span>İl *</span>
                    <select
                      name="cityId"
                      value={authorizedAreaFormState.cityId}
                      onChange={(e) => handleAuthorizedAreaCityChange(e.target.value)}
                      required
                    >
                      <option value="">İl Seçin</option>
                      {authorizedAreaIller.map((il) => (
                        <option key={il.id || il._id} value={il.id || il._id}>
                          {il.name || il.ad}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                
                {authorizedAreaFormState.cityId && (
                  <div className="form-field" style={{ marginTop: '20px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '12px', display: 'block' }}>
                      İlçeler
                    </span>
                    {authorizedAreaDistrictsLoading ? (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '20px', color: 'var(--primary-blue)', marginBottom: '8px' }}></i>
                        <div style={{ color: 'var(--text-gray)', fontSize: '13px' }}>İlçeler yükleniyor...</div>
                      </div>
                    ) : authorizedAreaDistricts.length > 0 ? (
                      <div style={{ 
                        maxHeight: '300px', 
                        overflowY: 'auto', 
                        border: '1px solid var(--border-color)', 
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        {authorizedAreaDistricts.map((district) => {
                          const districtId = district.id || district._id;
                          const check = getDistrictAuthCheck(districtId);
                          const isAuthorized = check?.yetkili || false;
                          const authorizedService = check?.techService;
                          const isSelected = authorizedAreaFormState.selectedDistricts.includes(String(districtId));
                          const otherTsBlocks =
                            isAuthorized &&
                            authorizedService &&
                            String(authorizedService.id) !== String(id);
                          const sel = authorizedAreaFormState.areaSelection || emptySelection();
                          const hasTreeSelection =
                            (sel.leafIds?.length || 0) > 0 || (sel.productIds?.length || 0) > 0;
                          const isDisabled =
                            otherTsBlocks &&
                            (check?.tamYetki || (hasTreeSelection && isAuthorized));
                          
                          const isReassigning =
                            reassignLoadingDistrictId != null &&
                            String(reassignLoadingDistrictId) === String(districtId);

                          return (
                            <div
                              key={districtId}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px',
                                marginBottom: '4px',
                                opacity: isDisabled ? 0.85 : 1,
                                backgroundColor: isSelected ? 'var(--primary-light)' : 'transparent',
                                borderRadius: '4px',
                              }}
                            >
                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flex: 1,
                                  minWidth: 0,
                                  cursor: isDisabled ? 'default' : 'pointer',
                                  margin: 0,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleDistrictCheckboxChange(String(districtId), e.target.checked)
                                  }
                                  disabled={isDisabled}
                                  style={{ marginRight: '8px', flexShrink: 0 }}
                                />
                                <span
                                  style={{
                                    fontSize: '14px',
                                    color: isDisabled ? 'var(--text-light)' : 'var(--text-dark)',
                                  }}
                                >
                                  {district.adi || district.ad || district.name || 'İsimsiz İlçe'}
                                  {otherTsBlocks && (
                                    <span
                                      style={{
                                        marginLeft: '8px',
                                        fontSize: '12px',
                                        color: 'var(--text-gray)',
                                      }}
                                    >
                                      {check?.kismiYetki
                                        ? 'Kısmi yetki'
                                        : `${authorizedService.ad || 'Bilinmeyen Servis'} yetkili`}
                                    </span>
                                  )}
                                </span>
                              </label>
                              <button
                                type="button"
                                className="category-action-btn"
                                title="Yetki özeti"
                                style={{ flexShrink: 0, padding: '4px 8px' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDistrictInfoModal({
                                    districtId,
                                    districtName:
                                      district.adi || district.ad || district.name || 'İlçe',
                                  });
                                }}
                              >
                                <i className="fas fa-circle-info" aria-hidden="true" />
                              </button>
                              {otherTsBlocks && authorizedService && (
                                <button
                                  type="button"
                                  className="secondary-btn"
                                  disabled={isReassigning}
                                  style={{ flexShrink: 0, fontSize: '12px', padding: '4px 10px' }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDistrictReassign(districtId);
                                  }}
                                >
                                  {isReassigning ? (
                                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                                  ) : (
                                    'Atama yap'
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-gray)', fontSize: '14px' }}>
                        Bu ile ait ilçe bulunamadı
                      </div>
                    )}
                  </div>
                )}

                <div className="form-field" style={{ marginTop: '20px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '12px', display: 'block' }}>
                    Yetkili Ürün Kategorileri
                  </span>
                  <div style={{ fontSize: '13px', color: 'var(--text-gray)', marginBottom: '8px' }}>
                    Grup veya ürün seçin; boş bırakırsanız tüm ürünler.
                  </div>
                  <div
                    style={{
                      maxHeight: '280px',
                      overflowY: 'auto',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      padding: '8px 6px',
                      backgroundColor: 'var(--bg-light)',
                    }}
                  >
                    <AuthorizedAreaProductTree
                      categories={authorizedAreaProductCategories}
                      value={authorizedAreaFormState.areaSelection}
                      onChange={(areaSelection) =>
                        setAuthorizedAreaFormState((prev) => ({ ...prev, areaSelection }))
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="secondary-btn" 
                  onClick={closeAuthorizedAreaModal}
                >
                  İptal
                </button>
                <button 
                  type="button" 
                  className="primary-btn" 
                  onClick={handleAuthorizedAreaSave}
                  disabled={(() => {
                    // Eğer il seçili değilse disabled
                    if (!authorizedAreaFormState.cityId) {
                      return true;
                    }
                    
                    // Bu il için daha önce eklenmiş ilçeleri kontrol et
                    const existingAreasForCity = serviceAreas.filter(area => {
                      const areaCityId = area.cityId || area.il?.id || area.il?._id;
                      return String(areaCityId) === String(authorizedAreaFormState.cityId);
                    });
                    
                    // Eğer bu il için daha önce eklenmiş ilçe varsa, buton her zaman aktif (silme işlemi yapılabilir)
                    if (existingAreasForCity.length > 0) {
                      return false;
                    }
                    
                    // Eğer bu il için daha önce eklenmiş ilçe yoksa, sadece seçili ilçe varsa aktif
                    return authorizedAreaFormState.selectedDistricts.length === 0;
                  })()}
                >
                  <i className="fas fa-save"></i>
                  Yetki Alanı Ekle
                </button>
              </div>
            </div>
          </div>
        )}

        {districtInfoModal && (
          <DistrictAuthorizationInfoModal
            isOpen={Boolean(districtInfoModal)}
            districtId={districtInfoModal.districtId}
            districtName={districtInfoModal.districtName}
            targetTechServiceId={id}
            cityId={authorizedAreaFormState.cityId}
            onClose={() => setDistrictInfoModal(null)}
            onChanged={() => {
              setDistrictChecksRefreshKey((k) => k + 1);
              if (id) loadServiceAreas(id);
            }}
          />
        )}

    </main>
  </>
  );
};

// LocationPickerMap Component (aynı kod)
const LocationPickerMap = ({ center, zoom, onZoomChange, pinVisible, pinPosition, onPinDragEnd, mapInstanceRef: parentMapInstanceRef, onCenterChange, markerRef: parentMarkerRef }) => {
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const markerListenersRef = React.useRef([]);
  const listenersRef = React.useRef([]);
  const isUpdatingZoomRef = React.useRef(false);
  const isUpdatingCenterRef = React.useRef(false);
  const onZoomChangeRef = React.useRef(onZoomChange);
  const onPinDragEndRef = React.useRef(onPinDragEnd);
  const onCenterChangeRef = React.useRef(onCenterChange);

  React.useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  }, [onZoomChange]);

  React.useEffect(() => {
    onPinDragEndRef.current = onPinDragEnd;
  }, [onPinDragEnd]);

  React.useEffect(() => {
    onCenterChangeRef.current = onCenterChange;
  }, [onCenterChange]);

  React.useEffect(() => {
    if (parentMapInstanceRef && mapInstanceRef.current) {
      parentMapInstanceRef.current = mapInstanceRef.current;
    }
  }, [parentMapInstanceRef]);

  useEffect(() => {
    if (!window.google || !window.google.maps || !mapInstanceRef.current) {
      return;
    }

    const currentZoom = mapInstanceRef.current.getZoom() || zoom;
    const shouldShowPin = pinVisible && currentZoom >= 10;

    if (shouldShowPin && pinPosition.lat !== null && pinPosition.lng !== null) {
      if (!markerRef.current) {
        const markerPosition = new window.google.maps.LatLng(pinPosition.lat, pinPosition.lng);
        
        markerRef.current = new window.google.maps.Marker({
          position: markerPosition,
          map: mapInstanceRef.current,
          icon: null,
          draggable: true,
          title: 'Konum işaretçisi',
          zIndex: 1000,
          animation: null,
        });

        if (parentMarkerRef) {
          parentMarkerRef.current = markerRef.current;
        }

        const dragEndListener = markerRef.current.addListener('dragend', () => {
          if (markerRef.current && onPinDragEndRef.current) {
            const position = markerRef.current.getPosition();
            if (position) {
              onPinDragEndRef.current(position.lat(), position.lng());
            }
          }
        });
        markerListenersRef.current.push(dragEndListener);
      } else {
        const newPosition = new window.google.maps.LatLng(pinPosition.lat, pinPosition.lng);
        markerRef.current.setPosition(newPosition);
        markerRef.current.setVisible(true);
      }
    } else {
      if (markerRef.current) {
        markerRef.current.setVisible(false);
      }
    }

    return () => {
      if (markerListenersRef.current.length > 0 && window.google && window.google.maps) {
        markerListenersRef.current.forEach(listener => {
          window.google.maps.event.removeListener(listener);
        });
        markerListenersRef.current = [];
      }
    };
  }, [pinVisible, pinPosition, zoom, parentMarkerRef]);

  useEffect(() => {
    if (!window.google || !window.google.maps || !mapRef.current) {
      return;
    }

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: zoom,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });

      if (parentMapInstanceRef) {
        parentMapInstanceRef.current = mapInstanceRef.current;
      }

      const zoomChangedListener = mapInstanceRef.current.addListener('zoom_changed', () => {
        if (!isUpdatingZoomRef.current && mapInstanceRef.current && onZoomChangeRef.current) {
          const newZoom = mapInstanceRef.current.getZoom();
          if (newZoom !== null && newZoom !== undefined) {
            onZoomChangeRef.current(newZoom);
            
            if (markerRef.current) {
              const shouldShowPin = pinVisible && newZoom >= 10;
              markerRef.current.setVisible(shouldShowPin);
            }
          }
        }
      });
      listenersRef.current.push(zoomChangedListener);

      const dragEndListener = mapInstanceRef.current.addListener('dragend', () => {
        if (!isUpdatingCenterRef.current && mapInstanceRef.current && onCenterChangeRef.current) {
          const newCenter = mapInstanceRef.current.getCenter();
          if (newCenter) {
            onCenterChangeRef.current(newCenter.lat(), newCenter.lng());
          }
        }
      });
      listenersRef.current.push(dragEndListener);

      const idleListener = mapInstanceRef.current.addListener('idle', () => {
        if (!isUpdatingZoomRef.current && mapInstanceRef.current && onZoomChangeRef.current) {
          const newZoom = mapInstanceRef.current.getZoom();
          if (newZoom !== null && newZoom !== undefined) {
            onZoomChangeRef.current(newZoom);
            
            if (markerRef.current) {
              const shouldShowPin = pinVisible && newZoom >= 10;
              markerRef.current.setVisible(shouldShowPin);
            }
          }
        }
        if (!isUpdatingCenterRef.current && mapInstanceRef.current && onCenterChangeRef.current) {
          const newCenter = mapInstanceRef.current.getCenter();
          if (newCenter) {
            onCenterChangeRef.current(newCenter.lat(), newCenter.lng());
          }
        }
      });
      listenersRef.current.push(idleListener);
    }

    return () => {
      if (window.google && window.google.maps && listenersRef.current.length > 0) {
        listenersRef.current.forEach(listener => {
          window.google.maps.event.removeListener(listener);
        });
        listenersRef.current = [];
      }
    };
  }, [center, zoom, parentMapInstanceRef, pinVisible]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      const currentCenter = mapInstanceRef.current.getCenter();
      const currentZoom = mapInstanceRef.current.getZoom();
      const centerChanged = currentCenter && (Math.abs(currentCenter.lat() - center.lat) > 0.0001 || Math.abs(currentCenter.lng() - center.lng) > 0.0001);
      const zoomChanged = Math.abs(currentZoom - zoom) > 0.1;

      if (centerChanged) {
        isUpdatingCenterRef.current = true;
        mapInstanceRef.current.setCenter(center);
        setTimeout(() => {
          isUpdatingCenterRef.current = false;
        }, 100);
      }

      if (zoomChanged) {
        isUpdatingZoomRef.current = true;
        mapInstanceRef.current.setZoom(zoom);
        setTimeout(() => {
          isUpdatingZoomRef.current = false;
        }, 100);
      }
    }
  }, [center, zoom]);

  return (
    <div ref={mapRef} className="location-map-container" style={{ flex: 1, width: '100%', height: '100%' }}>
    </div>
  );
};

export default TechServiceFormPage;
